import {
  AlignCenter,
  ArrowDown,
  ArrowUp,
  CalendarDays,
  Check,
  Circle,
  CircleAlert,
  ClipboardCopy,
  ClipboardPaste,
  Clock3,
  Code2,
  ContactRound,
  Copy,
  Globe2,
  GalleryVerticalEnd,
  GripHorizontal,
  Image,
  MessageCircle,
  Minus,
  MousePointer2,
  Phone,
  Plus,
  Square,
  Tag,
  Trash2,
  Type,
  Video,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { SlideRenderer } from "@qassist/slide-renderer";
import { ColorPicker } from "./ColorPicker.js";
import { StaticImagePicker } from "./StaticImagePicker.js";
import { previewScreenPresets } from "./ScreenPreview.js";
import {
  widgetSpacingKeys,
  widgetSpacingStyles,
  widgetSpacingFromStyles,
  type WidgetSpacing,
  defaultCarouselSettings,
  slideTypeDefinitionSchema,
  supportedFontFamilies,
  type FieldDefinition,
  type FieldType,
  type CarouselSettings,
  type LayoutNode,
  type SlideFieldValues,
  type SlideTypeDefinition,
} from "@qassist/schemas";

type BuilderElementType =
  | "text"
  | "label"
  | "verticalLabel"
  | "square"
  | "circle"
  | "line"
  | "textarea"
  | "html"
  | "image"
  | "logo"
  | "ticker"
  | "video"
  | "youtube"
  | "youtubeList"
  | "digitalTime"
  | "displayDate"
  | "analogClock"
  | "qrCall"
  | "qrUrl"
  | "qrWhatsApp"
  | "qrVCard"
  | "imageCarousel"
  | "textCarousel"
  | "contentCarousel"
  | "wordpressPosts"
  | "wordpressPostQr"
  | "staticImage";
type ArrangePreset = "free" | "centered" | "split-left" | "split-right" | "hero";
type DimensionUnit = "%" | "px";
type PositionMode = "free" | "center";
type ResizeHandle = "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";
const resizeHandleLabels: Record<ResizeHandle, string> = {
  n: "top",
  ne: "top right",
  e: "right",
  se: "bottom right",
  s: "bottom",
  sw: "bottom left",
  w: "left",
  nw: "top left",
};
type BuilderAnimation =
  | "none"
  | "fade-in"
  | "blur-in"
  | "slide-in-left"
  | "slide-in-right"
  | "slide-in-up"
  | "slide-in-down"
  | "zoom-in-subtle"
  | "zoom-in"
  | "bounce-in"
  | "rotate-in"
  | "flip-in"
  | "reveal-up";

interface BuilderElement {
  id: string;
  key: string;
  label: string;
  type: BuilderElementType;
  required: boolean;
  previewText: string;
  htmlContent: string;
  x: number | null;
  y: number | null;
  right: number | null;
  bottom: number | null;
  width: number | null;
  height: number | null;
  xUnit: DimensionUnit;
  yUnit: DimensionUnit;
  rightUnit: DimensionUnit;
  bottomUnit: DimensionUnit;
  widthUnit: DimensionUnit;
  heightUnit: DimensionUnit;
  positionMode: PositionMode;
  centerX: number;
  centerY: number;
  centerXUnit: DimensionUnit;
  centerYUnit: DimensionUnit;
  zIndex: number;
  color: string;
  backgroundColor: string;
  fontSize: number;
  fontWeight: number;
  textAlign: "left" | "center" | "right";
  opacity: number;
  borderRadius: number;
  padding: number;
  paddingUnit: DimensionUnit;
  spacing?: WidgetSpacing | undefined;
  legacySpacingStyles?: LayoutNode["styles"];
  objectFit: "cover" | "contain" | "fill";
  objectPosition: string;
  borderWidth: number;
  borderColor: string;
  fontFamily: string;
  fontStyle: "normal" | "italic";
  textDecoration: "none" | "underline";
  textTransform: "none" | "uppercase" | "lowercase" | "capitalize";
  lineHeight: number;
  letterSpacing: number;
  textFit: "fixed" | "shrink";
  textWrap: "wrap" | "nowrap" | "preserve";
  minFontSize: number;
  tickerDirection: "left" | "right";
  tickerVerticalAlign: "flex-start" | "center" | "flex-end";
  tickerSpeed: number;
  tickerSeparator: string;
  tickerSeparatorColor: string;
  tickerSeparatorSpacing: number;
  tickerSequenceGap: number;
  digitalShowSeconds: boolean;
  digitalUse24Hour: boolean;
  dateFormat: string;
  analogShowHourMarkers: boolean;
  analogShowSecondHand: boolean;
  analogShowNumbers: boolean;
  analogNumberColor: string;
  analogNumberFontFamily: string;
  analogNumberFontSize: number;
  analogNumberFontWeight: number;
  analogNumberRadius: number;
  analogMarkerColor: string;
  analogHourHandColor: string;
  analogMinuteHandColor: string;
  analogSecondHandColor: string;
  analogMarkerLength: number;
  analogHourHandLength: number;
  analogMinuteHandLength: number;
  analogSecondHandLength: number;
  analogHandThickness: number;
  carousel: CarouselSettings;
  wordpressSourceBinding: string;
  staticImageAssetId: string;
  verticalLabelOrientation: "clockwise" | "counterclockwise" | "upright";
  animation: BuilderAnimation;
  animationDurationMs: number;
  animationDelayMs: number;
  animationIteration: "once" | "infinite";
}

interface TemplateGuiBuilderProps {
  initialDefinition: SlideTypeDefinition;
  mode: "create" | "edit";
  clientOptions?: Array<{ id: string; name: string }>;
  saving: boolean;
  error?: string;
  onCancel(): void;
  onSave(definition: SlideTypeDefinition): void;
}

let builderElementClipboard: BuilderElement | null = null;

const sampleImage = "/demo-logo.svg";
const sampleVideo = "";
const sampleYoutube = "https://www.youtube.com/watch?v=M7lc1UVf-VE";
const defaultHtmlDocument =
  '<!doctype html>\n<html lang="en">\n<head>\n  <meta charset="utf-8">\n  <meta name="viewport" content="width=device-width, initial-scale=1">\n  <style>\n    html, body { width: 100%; height: 100%; margin: 0; }\n    body { display: grid; place-items: center; background: #101827; color: white; font-family: Arial, sans-serif; }\n  </style>\n</head>\n<body>\n  <h1>Custom HTML</h1>\n</body>\n</html>';
const previewScreenWidth = 1960;
const fontFamilies = supportedFontFamilies;
const mediaPositions = [
  "center",
  "top",
  "bottom",
  "left",
  "right",
  "top left",
  "top right",
  "bottom left",
  "bottom right",
] as const;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}

function safeKey(value: string): string {
  return (
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "untitled-template"
  );
}

function safeFieldKey(value: string): string {
  const normalized = value
    .trim()
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const withLetterPrefix = /^[A-Za-z]/.test(normalized) ? normalized : `field_${normalized}`;
  return withLetterPrefix || "field";
}

function cloneBuilderElement(element: BuilderElement): BuilderElement {
  return JSON.parse(JSON.stringify(element)) as BuilderElement;
}

function nextElementCopyKey(sourceKey: string, elements: BuilderElement[]): string {
  const existing = new Set(elements.map((element) => safeFieldKey(element.key)));
  const base = safeFieldKey(`${sourceKey}_copy`);
  if (!existing.has(base)) return base;
  let suffix = 2;
  while (existing.has(safeFieldKey(`${base}_${suffix}`))) suffix += 1;
  return safeFieldKey(`${base}_${suffix}`);
}

function nextElementCopyLabel(sourceLabel: string, elements: BuilderElement[]): string {
  const existing = new Set(elements.map((element) => element.label));
  const base = `${sourceLabel} copy`;
  if (!existing.has(base)) return base;
  let suffix = 2;
  while (existing.has(`${base} ${suffix}`)) suffix += 1;
  return `${base} ${suffix}`;
}

function supportedType(type: FieldType, node?: LayoutNode): BuilderElementType {
  if (node?.type === "staticImage") return "staticImage";
  if (node?.type === "wordpressPostQr") return "wordpressPostQr";
  if (node?.type === "wordpressPosts" || type === "wordpressPosts") return "wordpressPosts";
  if (node?.type === "label") return "label";
  if (node?.type === "verticalLabel") return "verticalLabel";
  if (node?.type === "square" || node?.type === "circle" || node?.type === "line") return node.type;
  if (node?.type === "imageCarousel" || type === "imageCarousel") return "imageCarousel";
  if (node?.type === "textCarousel" || type === "textCarousel") return "textCarousel";
  if (node?.type === "contentCarousel" || type === "contentCarousel") return "contentCarousel";
  if (node?.type === "ticker") return "ticker";
  if (node?.type === "digitalTime") return "digitalTime";
  if (node?.type === "displayDate") return "displayDate";
  if (node?.type === "analogClock") return "analogClock";
  if (node?.type === "qrCall" || type === "phone") return "qrCall";
  if (node?.type === "qrUrl") return "qrUrl";
  if (node?.type === "qrWhatsApp" || type === "whatsapp") return "qrWhatsApp";
  if (node?.type === "qrVCard" || type === "vcard") return "qrVCard";
  return ["text", "textarea", "html", "image", "logo", "video", "youtube", "youtubeList"].includes(
    type,
  )
    ? (type as BuilderElementType)
    : "text";
}

function layoutNodesByBinding(layout: LayoutNode): Map<string, LayoutNode> {
  const result = new Map<string, LayoutNode>();
  const visit = (node: LayoutNode): void => {
    if (node.binding) result.set(node.binding, node);
    node.children?.forEach(visit);
  };
  visit(layout);
  return result;
}

function optionalDimension(
  value: string | number | undefined,
  fallbackUnit: DimensionUnit = "%",
): { value: number | null; unit: DimensionUnit } {
  if (typeof value === "number")
    return { value: Number.isFinite(value) ? value : null, unit: "px" };
  if (typeof value !== "string") return { value: null, unit: fallbackUnit };
  const parsed = Number.parseFloat(value);
  return {
    value: Number.isFinite(parsed) ? parsed : null,
    unit: value.trim().endsWith("px") ? "px" : "%",
  };
}

function initialAxis(
  startValue: string | number | undefined,
  endValue: string | number | undefined,
  sizeValue: string | number | undefined,
  defaultStart: number,
  defaultSize: number,
): {
  start: number | null;
  end: number | null;
  size: number | null;
  startUnit: DimensionUnit;
  endUnit: DimensionUnit;
  sizeUnit: DimensionUnit;
} {
  const parsedStart = optionalDimension(startValue);
  const parsedEnd = optionalDimension(endValue);
  const parsedSize = optionalDimension(sizeValue);
  let start = parsedStart.value;
  let end = parsedEnd.value;
  let size = parsedSize.value;
  const definedCount = [start, end, size].filter((value) => value !== null).length;

  if (definedCount === 3) end = null;
  if (definedCount === 0) {
    start = defaultStart;
    size = defaultSize;
  } else if (definedCount === 1) {
    if (start !== null) size = Math.min(defaultSize, Math.max(0.5, 100 - start));
    else if (end !== null) size = Math.min(defaultSize, Math.max(0.5, 100 - end));
    else if (size !== null) start = Math.min(defaultStart, Math.max(0, 100 - size));
  }

  return {
    start,
    end,
    size,
    startUnit: parsedStart.unit,
    endUnit: parsedEnd.unit,
    sizeUnit: parsedSize.unit,
  };
}

interface ResolvedGeometry {
  left: number;
  top: number;
  width: number;
  height: number;
}

function resolvedAxis(
  start: number | null,
  startUnit: DimensionUnit,
  end: number | null,
  endUnit: DimensionUnit,
  size: number | null,
  sizeUnit: DimensionUnit,
  canvasSize: number,
): { start: number; size: number } {
  const asPercent = (value: number, unit: DimensionUnit): number =>
    unit === "%" ? value : (value / canvasSize) * 100;
  const startPercent = start === null ? null : asPercent(start, startUnit);
  const endPercent = end === null ? null : asPercent(end, endUnit);
  const sizePercent = size === null ? null : asPercent(size, sizeUnit);
  if (startPercent !== null && sizePercent !== null)
    return { start: startPercent, size: sizePercent };
  if (endPercent !== null && sizePercent !== null)
    return { start: 100 - endPercent - sizePercent, size: sizePercent };
  if (startPercent !== null && endPercent !== null)
    return { start: startPercent, size: 100 - startPercent - endPercent };
  if (startPercent !== null) return { start: startPercent, size: Math.min(25, 100 - startPercent) };
  if (endPercent !== null)
    return {
      start: Math.max(0, 75 - endPercent),
      size: Math.min(25, 100 - endPercent),
    };
  if (sizePercent !== null)
    return { start: Math.max(0, (100 - sizePercent) / 2), size: sizePercent };
  return { start: 0, size: 100 };
}

function resolvedGeometry(
  element: BuilderElement,
  canvasWidth: number,
  canvasHeight: number,
): ResolvedGeometry {
  const horizontal = resolvedAxis(
    element.x,
    element.xUnit,
    element.right,
    element.rightUnit,
    element.width,
    element.widthUnit,
    canvasWidth,
  );
  const vertical = resolvedAxis(
    element.y,
    element.yUnit,
    element.bottom,
    element.bottomUnit,
    element.height,
    element.heightUnit,
    canvasHeight,
  );
  if (element.positionMode === "center") {
    const centerX =
      element.centerXUnit === "%" ? element.centerX : (element.centerX / canvasWidth) * 100;
    const centerY =
      element.centerYUnit === "%" ? element.centerY : (element.centerY / canvasHeight) * 100;
    return {
      left: 50 + centerX - horizontal.size / 2,
      top: 50 + centerY - vertical.size / 2,
      width: horizontal.size,
      height: vertical.size,
    };
  }
  return {
    left: horizontal.start,
    top: vertical.start,
    width: horizontal.size,
    height: vertical.size,
  };
}

function axisIssue(
  start: number | null,
  startUnit: DimensionUnit,
  end: number | null,
  endUnit: DimensionUnit,
  size: number | null,
  sizeUnit: DimensionUnit,
  axisName: "horizontal" | "vertical",
  canvasSize: number,
): string | null {
  const labels =
    axisName === "horizontal"
      ? { start: "Left", end: "Right", size: "Width" }
      : { start: "Top", end: "Bottom", size: "Height" };
  if ([start, end].some((value) => value !== null && value < 0))
    return `${labels.start} and ${labels.end} cannot be negative.`;
  if (size !== null && size <= 0) return `${labels.size} must be greater than zero.`;

  const resolved = resolvedAxis(start, startUnit, end, endUnit, size, sizeUnit, canvasSize);
  if (!resolved || resolved.start < 0 || resolved.size <= 0 || resolved.start + resolved.size > 100)
    return `${labels.start}, ${labels.end}, and ${labels.size} must keep the box inside the canvas.`;
  return null;
}

function geometryIssue(
  element: BuilderElement,
  canvasWidth: number,
  canvasHeight: number,
): string | null {
  if (element.positionMode === "center") {
    const resolved = resolvedGeometry(element, canvasWidth, canvasHeight);
    if (
      resolved.left < 0 ||
      resolved.top < 0 ||
      resolved.left + resolved.width > 100 ||
      resolved.top + resolved.height > 100
    )
      return `${element.label || "Selected element"}: center offsets must keep the box inside the canvas.`;
    return null;
  }
  const horizontal = axisIssue(
    element.x,
    element.xUnit,
    element.right,
    element.rightUnit,
    element.width,
    element.widthUnit,
    "horizontal",
    canvasWidth,
  );
  const vertical = axisIssue(
    element.y,
    element.yUnit,
    element.bottom,
    element.bottomUnit,
    element.height,
    element.heightUnit,
    "vertical",
    canvasHeight,
  );
  const issue = horizontal ?? vertical;
  return issue ? `${element.label || "Selected element"}: ${issue}` : null;
}

function geometryStyles(
  element: BuilderElement,
  canvasWidth: number,
  canvasHeight: number,
): Record<string, string> {
  const styles: Record<string, string> = {};
  const dimension = (value: number, unit: DimensionUnit): string => `${value}${unit}`;
  const offset = (value: number, unit: DimensionUnit): string =>
    value < 0 ? `- ${Math.abs(value)}${unit}` : `+ ${value}${unit}`;
  if (element.positionMode === "center") {
    const width = element.width ?? 25;
    const height = element.height ?? 25;
    styles.left = `calc(50% ${offset(element.centerX, element.centerXUnit)} - ${width / 2}${element.widthUnit})`;
    styles.top = `calc(50% ${offset(element.centerY, element.centerYUnit)} - ${height / 2}${element.heightUnit})`;
    styles.width = dimension(width, element.widthUnit);
    styles.height = dimension(height, element.heightUnit);
    return styles;
  }
  const applyAxis = (
    start: number | null,
    startUnit: DimensionUnit,
    end: number | null,
    endUnit: DimensionUnit,
    size: number | null,
    sizeUnit: DimensionUnit,
    startProperty: "left" | "top",
    endProperty: "right" | "bottom",
    sizeProperty: "width" | "height",
    canvasSize: number,
  ): void => {
    const definedCount = [start, end, size].filter((value) => value !== null).length;
    if (definedCount >= 2) {
      if (start !== null) styles[startProperty] = dimension(start, startUnit);
      if (end !== null && !(start !== null && size !== null))
        styles[endProperty] = dimension(end, endUnit);
      if (size !== null) styles[sizeProperty] = dimension(size, sizeUnit);
      return;
    }
    const resolved = resolvedAxis(start, startUnit, end, endUnit, size, sizeUnit, canvasSize);
    styles[startProperty] = `${resolved.start}%`;
    styles[sizeProperty] = `${resolved.size}%`;
  };
  applyAxis(
    element.x,
    element.xUnit,
    element.right,
    element.rightUnit,
    element.width,
    element.widthUnit,
    "left",
    "right",
    "width",
    canvasWidth,
  );
  applyAxis(
    element.y,
    element.yUnit,
    element.bottom,
    element.bottomUnit,
    element.height,
    element.heightUnit,
    "top",
    "bottom",
    "height",
    canvasHeight,
  );
  return styles;
}

function repositionPatch(
  element: BuilderElement,
  left: number,
  top: number,
  canvasWidth: number,
  canvasHeight: number,
): Partial<BuilderElement> {
  const geometry = resolvedGeometry(element, canvasWidth, canvasHeight);
  if (!geometry) return {};
  const patch: Partial<BuilderElement> = {};
  const fromPercent = (value: number, unit: DimensionUnit, canvasSize: number): number =>
    unit === "%" ? value : (value / 100) * canvasSize;

  if (element.positionMode === "center") {
    return {
      centerX: fromPercent(left + geometry.width / 2 - 50, element.centerXUnit, canvasWidth),
      centerY: fromPercent(top + geometry.height / 2 - 50, element.centerYUnit, canvasHeight),
    };
  }

  if (element.width === null) {
    patch.x = fromPercent(left, element.xUnit, canvasWidth);
    patch.right = fromPercent(100 - left - geometry.width, element.rightUnit, canvasWidth);
  } else if (element.x === null) {
    patch.right = fromPercent(100 - left - geometry.width, element.rightUnit, canvasWidth);
  } else {
    patch.x = fromPercent(left, element.xUnit, canvasWidth);
  }

  if (element.height === null) {
    patch.y = fromPercent(top, element.yUnit, canvasHeight);
    patch.bottom = fromPercent(100 - top - geometry.height, element.bottomUnit, canvasHeight);
  } else if (element.y === null) {
    patch.bottom = fromPercent(100 - top - geometry.height, element.bottomUnit, canvasHeight);
  } else {
    patch.y = fromPercent(top, element.yUnit, canvasHeight);
  }

  return patch;
}

function resizedGeometry(
  origin: ResolvedGeometry,
  handle: ResizeHandle,
  deltaX: number,
  deltaY: number,
): ResolvedGeometry {
  const minimum = 0.5;
  let left = origin.left;
  let top = origin.top;
  let right = origin.left + origin.width;
  let bottom = origin.top + origin.height;

  if (handle.includes("w")) left = clamp(origin.left + deltaX, 0, right - minimum);
  if (handle.includes("e")) right = clamp(right + deltaX, left + minimum, 100);
  if (handle.includes("n")) top = clamp(origin.top + deltaY, 0, bottom - minimum);
  if (handle.includes("s")) bottom = clamp(bottom + deltaY, top + minimum, 100);

  return { left, top, width: right - left, height: bottom - top };
}

function resizePatch(
  element: BuilderElement,
  geometry: ResolvedGeometry,
  canvasWidth: number,
  canvasHeight: number,
): Partial<BuilderElement> {
  const fromPercent = (value: number, unit: DimensionUnit, canvasSize: number): number =>
    unit === "%" ? value : (value / 100) * canvasSize;
  const width = fromPercent(geometry.width, element.widthUnit, canvasWidth);
  const height = fromPercent(geometry.height, element.heightUnit, canvasHeight);

  if (element.positionMode === "center") {
    return {
      width,
      height,
      centerX: fromPercent(
        geometry.left + geometry.width / 2 - 50,
        element.centerXUnit,
        canvasWidth,
      ),
      centerY: fromPercent(
        geometry.top + geometry.height / 2 - 50,
        element.centerYUnit,
        canvasHeight,
      ),
    };
  }

  const horizontal =
    element.x !== null || element.right === null
      ? {
          x: fromPercent(geometry.left, element.xUnit, canvasWidth),
          right: null,
        }
      : {
          x: null,
          right: fromPercent(100 - geometry.left - geometry.width, element.rightUnit, canvasWidth),
        };
  const vertical =
    element.y !== null || element.bottom === null
      ? {
          y: fromPercent(geometry.top, element.yUnit, canvasHeight),
          bottom: null,
        }
      : {
          y: null,
          bottom: fromPercent(
            100 - geometry.top - geometry.height,
            element.bottomUnit,
            canvasHeight,
          ),
        };
  return { ...horizontal, ...vertical, width, height };
}

function numberValue(value: string | number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function initialElements(definition: SlideTypeDefinition): BuilderElement[] {
  const nodes = layoutNodesByBinding(definition.layout);
  return definition.fields.slice(0, 80).map((field, index) => {
    const node = nodes.get(field.key);
    const styles = node?.styles ?? {};
    const type = supportedType(field.type, node);
    const column = index % 2;
    const row = Math.floor(index / 2);
    const horizontal = initialAxis(styles.left, styles.right, styles.width, 5 + column * 49, 42);
    const vertical = initialAxis(
      styles.top,
      styles.bottom,
      styles.height,
      6 + row * 21,
      type === "textarea"
        ? 26
        : type === "text"
          ? 16
          : type === "html"
            ? 42
            : type.endsWith("Carousel") || type === "wordpressPosts"
              ? 48
              : 36,
    );
    return {
      id: node?.id ?? crypto.randomUUID(),
      key: field.key,
      label: field.label,
      type,
      required: field.required,
      previewText:
        field.previewText ??
        ((type === "label" || type === "verticalLabel") && typeof field.defaultValue === "string"
          ? field.defaultValue
          : undefined) ??
        (type === "textarea"
          ? "A clear supporting message that stays inside its editable box."
          : type === "ticker"
            ? "First news headline\nSecond news headline\nThird news headline"
            : type === "textCarousel" || type === "contentCarousel" || type === "wordpressPosts"
              ? "Welcome | A clear supporting description\nExplore | A second carousel item\nConnect | A final call to action"
              : field.label || "Sample heading"),
      htmlContent:
        type === "html" && typeof field.defaultValue === "string"
          ? field.defaultValue
          : defaultHtmlDocument,
      x: horizontal.start,
      y: vertical.start,
      right: horizontal.end,
      bottom: vertical.end,
      width: horizontal.size,
      height: vertical.size,
      xUnit: horizontal.startUnit,
      yUnit: vertical.startUnit,
      rightUnit: horizontal.endUnit,
      bottomUnit: vertical.endUnit,
      widthUnit: horizontal.sizeUnit,
      heightUnit: vertical.sizeUnit,
      positionMode: node?.positioning?.mode ?? "free",
      centerX: node?.positioning?.horizontalOffset.value ?? 0,
      centerY: node?.positioning?.verticalOffset.value ?? 0,
      centerXUnit: node?.positioning?.horizontalOffset.unit ?? "%",
      centerYUnit: node?.positioning?.verticalOffset.unit ?? "%",
      zIndex: numberValue(styles.zIndex, index + 1),
      color:
        typeof styles.color === "string"
          ? styles.color
          : type.startsWith("qr")
            ? "#111111"
            : "#ffffff",
      backgroundColor:
        typeof styles.backgroundColor === "string"
          ? styles.backgroundColor
          : type.startsWith("qr")
            ? "#ffffff"
            : "transparent",
      fontSize: numberValue(
        styles.fontSize,
        type === "textarea" ? 34 : type === "ticker" ? 28 : 56,
      ),
      fontWeight: numberValue(styles.fontWeight, type === "textarea" ? 400 : 700),
      textAlign:
        styles.textAlign === "center" || styles.textAlign === "right" ? styles.textAlign : "left",
      opacity: numberValue(styles.opacity, 1),
      borderRadius: numberValue(
        styles.borderRadius,
        type === "image" || type === "video" ? 24 : type === "analogClock" ? 200 : 0,
      ),
      padding:
        optionalDimension(styles.padding, "px").value ??
        (type === "text" || type === "textarea" || type === "label" || type === "ticker" ? 12 : 0),
      paddingUnit: optionalDimension(styles.padding, "px").unit,
      spacing: node?.spacing,
      legacySpacingStyles: Object.fromEntries(
        Object.entries(styles).filter(
          ([key]) =>
            key === "padding" ||
            key === "margin" ||
            widgetSpacingKeys.includes(key as (typeof widgetSpacingKeys)[number]),
        ),
      ),
      objectFit:
        styles.objectFit === "contain" || styles.objectFit === "fill"
          ? styles.objectFit
          : type === "logo"
            ? "contain"
            : "cover",
      objectPosition: typeof styles.objectPosition === "string" ? styles.objectPosition : "center",
      borderWidth: numberValue(styles.borderWidth, type === "analogClock" ? 4 : 0),
      borderColor: typeof styles.borderColor === "string" ? styles.borderColor : "#ffffff",
      fontFamily:
        typeof styles.fontFamily === "string" &&
        fontFamilies.includes(styles.fontFamily as (typeof fontFamilies)[number])
          ? styles.fontFamily
          : "Inter",
      fontStyle: styles.fontStyle === "italic" ? "italic" : "normal",
      textDecoration: styles.textDecoration === "underline" ? "underline" : "none",
      textTransform:
        styles.textTransform === "uppercase" ||
        styles.textTransform === "lowercase" ||
        styles.textTransform === "capitalize"
          ? styles.textTransform
          : "none",
      lineHeight: numberValue(styles.lineHeight, 1.2),
      letterSpacing: numberValue(styles.letterSpacing, 0),
      textFit: node?.textFit ?? "fixed",
      textWrap:
        node?.textWrap ??
        (styles.whiteSpace === "nowrap"
          ? "nowrap"
          : styles.whiteSpace === "pre-wrap"
            ? "preserve"
            : "wrap"),
      minFontSize: node?.minFontSize ?? 12,
      tickerDirection: node?.ticker?.direction ?? "left",
      tickerVerticalAlign:
        styles.alignItems === "flex-start" || styles.alignItems === "flex-end"
          ? styles.alignItems
          : "center",
      tickerSpeed: node?.ticker?.speed ?? 80,
      tickerSeparator: node?.ticker?.separator ?? "◆",
      tickerSeparatorColor: node?.ticker?.separatorColor ?? "#ffffff",
      tickerSeparatorSpacing: node?.ticker?.separatorSpacing ?? 24,
      tickerSequenceGap: node?.ticker?.sequenceGap ?? 80,
      digitalShowSeconds: node?.digitalClock?.showSeconds ?? true,
      digitalUse24Hour: node?.digitalClock?.use24Hour ?? false,
      dateFormat: node?.dateFormat ?? "dddd, MMMM D, YYYY",
      analogShowHourMarkers: node?.analogClock?.showHourMarkers ?? true,
      analogShowSecondHand: node?.analogClock?.showSecondHand ?? true,
      analogShowNumbers: node?.analogClock?.showNumbers ?? false,
      analogNumberColor: node?.analogClock?.numberColor ?? "#ffffff",
      analogNumberFontFamily: node?.analogClock?.numberFontFamily ?? "Inter, sans-serif",
      analogNumberFontSize: node?.analogClock?.numberFontSize ?? 24,
      analogNumberFontWeight: node?.analogClock?.numberFontWeight ?? 600,
      analogNumberRadius: node?.analogClock?.numberRadius ?? 36,
      analogMarkerColor: node?.analogClock?.markerColor ?? "#ffffff",
      analogHourHandColor: node?.analogClock?.hourHandColor ?? "#ffffff",
      analogMinuteHandColor: node?.analogClock?.minuteHandColor ?? "#ffffff",
      analogSecondHandColor: node?.analogClock?.secondHandColor ?? "#ef4444",
      analogMarkerLength: node?.analogClock?.markerLength ?? 8,
      analogHourHandLength: node?.analogClock?.hourHandLength ?? 25,
      analogMinuteHandLength: node?.analogClock?.minuteHandLength ?? 34,
      analogSecondHandLength: node?.analogClock?.secondHandLength ?? 38,
      analogHandThickness: node?.analogClock?.handThickness ?? 2.8,
      carousel: node?.carousel
        ? {
            ...node.carousel,
            title: { ...node.carousel.title },
            description: { ...node.carousel.description },
          }
        : {
            ...defaultCarouselSettings,
            title: { ...defaultCarouselSettings.title },
            description: { ...defaultCarouselSettings.description },
          },
      wordpressSourceBinding: node?.wordpressSourceBinding ?? "",
      staticImageAssetId: node?.staticImageAssetId ?? "",
      verticalLabelOrientation: node?.verticalLabelOrientation ?? "clockwise",
      animation: (node?.animation ?? "none") as BuilderAnimation,
      animationDurationMs: node?.animationDurationMs ?? 500,
      animationDelayMs: node?.animationDelayMs ?? 0,
      animationIteration: node?.animationIteration ?? "once",
    };
  });
}

function fieldDefinition(element: BuilderElement): FieldDefinition {
  const fieldType: FieldType =
    element.type === "label" ||
    element.type === "verticalLabel" ||
    element.type === "square" ||
    element.type === "circle" ||
    element.type === "line" ||
    element.type === "wordpressPostQr" ||
    element.type === "staticImage"
      ? "text"
      : element.type === "qrCall"
        ? "phone"
        : element.type === "qrUrl"
          ? "url"
          : element.type === "qrWhatsApp"
            ? "whatsapp"
            : element.type === "qrVCard"
              ? "vcard"
              : element.type === "digitalTime" || element.type === "analogClock"
                ? "time"
                : element.type === "displayDate"
                  ? "date"
                  : element.type === "ticker"
                    ? "tickerContent"
                    : element.type === "imageCarousel"
                      ? "imageCarousel"
                      : element.type === "textCarousel"
                        ? "textCarousel"
                        : element.type === "contentCarousel"
                          ? "contentCarousel"
                          : element.type;
  const administratorOwned = [
    "staticImage",
    "wordpressPostQr",
    "html",
    "digitalTime",
    "displayDate",
    "analogClock",
    "label",
    "verticalLabel",
    "square",
    "circle",
    "line",
  ].includes(element.type);
  const base = {
    key: safeFieldKey(element.key),
    label: element.label.trim() || "Untitled element",
    type: fieldType,
    required: administratorOwned ? false : element.required,
    localization: false,
    allowedClientOverrides: [],
    adminOnly: administratorOwned,
    ...(["label", "verticalLabel", "square", "circle", "line"].includes(element.type)
      ? {
          defaultValue:
            element.type === "label" || element.type === "verticalLabel" ? element.previewText : "",
        }
      : {}),
    ...([
      "text",
      "textarea",
      "label",
      "verticalLabel",
      "ticker",
      "textCarousel",
      "contentCarousel",
      "wordpressPosts",
    ].includes(element.type) && element.previewText.trim()
      ? { previewText: element.previewText }
      : {}),
  } satisfies FieldDefinition;
  if (element.type === "image" || element.type === "logo")
    return {
      ...base,
      accept: ["image/jpeg", "image/png", "image/webp"],
      maxFileSizeMb: 15,
    };
  if (element.type === "video")
    return {
      ...base,
      accept: ["video/mp4", "video/webm"],
      maxFileSizeMb: 100,
    };
  if (element.type === "youtube") return { ...base, placeholder: "YouTube video or playlist URL" };
  if (element.type === "youtubeList")
    return { ...base, helpText: "Client-managed YouTube video list with optional shuffle." };
  if (element.type === "html")
    return {
      ...base,
      defaultValue: element.htmlContent,
      placeholder: "<!doctype html>…",
      helpText:
        "Administrator-owned complete HTML document rendered without scripts, forms, frames, or navigation.",
      maxLength: 500_000,
    };
  if (["digitalTime", "displayDate", "analogClock"].includes(element.type)) return base;
  if (element.type === "ticker")
    return {
      ...base,
      helpText: "Choose manual headlines or an RSS source made available by the administrator.",
    };
  if (element.type === "imageCarousel")
    return {
      ...base,
      accept: ["image/jpeg", "image/png", "image/webp"],
      maxFileSizeMb: 15,
      helpText: "Add and order up to 100 images from this client's asset library.",
    };
  if (element.type === "textCarousel")
    return {
      ...base,
      helpText: "Add and order up to 100 title and description items.",
    };
  if (element.type === "wordpressPosts")
    return {
      ...base,
      helpText:
        "Enter the public HTTPS wp-json URL and REST post-type slug. Titles and excerpts refresh automatically.",
    };
  if (element.type === "contentCarousel")
    return {
      ...base,
      accept: ["image/jpeg", "image/png", "image/webp"],
      maxFileSizeMb: 15,
      helpText: "Add and order up to 100 image, title, and description cards.",
    };
  if (element.type === "qrCall") return { ...base, placeholder: "+974 5555 1234", maxLength: 25 };
  if (element.type === "qrUrl")
    return { ...base, placeholder: "https://example.com/service", maxLength: 2_000 };
  if (element.type === "qrWhatsApp")
    return { ...base, helpText: "Enter the WhatsApp number and prefilled message." };
  if (element.type === "qrVCard")
    return {
      ...base,
      helpText: "Create a downloadable VCF contact QR code.",
    };
  return { ...base, maxLength: element.type === "textarea" ? 1_000 : 240 };
}

function elementNode(
  element: BuilderElement,
  canvasWidth: number,
  canvasHeight: number,
): LayoutNode {
  const node = baseElementNode(element, canvasWidth, canvasHeight);
  return {
    ...node,
    styles: { ...node.styles, ...element.legacySpacingStyles },
    ...(element.spacing ? { spacing: element.spacing } : {}),
  };
}

function elementSpacing(element: BuilderElement): WidgetSpacing {
  return {
    ...widgetSpacingFromStyles({
      padding: `${element.padding}${element.paddingUnit}`,
      ...element.legacySpacingStyles,
    }),
    ...element.spacing,
  };
}

function baseElementNode(
  element: BuilderElement,
  canvasWidth: number,
  canvasHeight: number,
): LayoutNode {
  const type =
    element.type === "textarea" || element.type === "text"
      ? "text"
      : element.type === "ticker"
        ? "ticker"
        : element.type;
  const commonStyles = {
    position: "absolute",
    ...geometryStyles(element, canvasWidth, canvasHeight),
    zIndex: element.zIndex,
    opacity: element.opacity,
    borderRadius: element.borderRadius,
    overflow: "hidden",
    boxSizing: "border-box",
    backgroundColor: element.backgroundColor,
    borderWidth: element.borderWidth,
    borderStyle: "solid",
    borderColor: element.borderColor,
  } as const;
  if (type === "wordpressPostQr" || type === "staticImage")
    return {
      id: element.id,
      type,
      binding: safeFieldKey(element.key),
      ...(type === "wordpressPostQr"
        ? { wordpressSourceBinding: element.wordpressSourceBinding }
        : { staticImageAssetId: element.staticImageAssetId || undefined }),
      styles: {
        ...commonStyles,
        color: element.color,
        objectFit: element.objectFit,
        objectPosition: element.objectPosition,
        padding: `${element.padding}${element.paddingUnit}`,
      },
      positioning: {
        mode: element.positionMode,
        horizontalOffset: { value: element.centerX, unit: element.centerXUnit },
        verticalOffset: { value: element.centerY, unit: element.centerYUnit },
      },
      animation: element.animation,
      animationDurationMs: element.animationDurationMs,
      animationDelayMs: element.animationDelayMs,
      animationIteration: element.animationIteration,
    };
  if (type === "text" || type === "label" || type === "verticalLabel")
    return {
      id: element.id,
      type,
      binding: safeFieldKey(element.key),
      variant: element.type === "textarea" ? "description" : "title",
      ...(type === "verticalLabel"
        ? { verticalLabelOrientation: element.verticalLabelOrientation }
        : {}),
      styles: {
        ...commonStyles,
        color: element.color,
        fontSize: element.fontSize,
        fontWeight: element.fontWeight,
        fontFamily: element.fontFamily,
        fontStyle: element.fontStyle,
        textDecoration: element.textDecoration,
        textTransform: element.textTransform,
        lineHeight: element.lineHeight,
        letterSpacing: element.letterSpacing,
        textAlign: element.textAlign,
        padding: `${element.padding}${element.paddingUnit}`,
        whiteSpace:
          element.textWrap === "nowrap"
            ? "nowrap"
            : element.textWrap === "preserve"
              ? "pre-wrap"
              : "normal",
      },
      textFit: element.textFit,
      textWrap: element.textWrap,
      minFontSize: element.minFontSize,
      positioning: {
        mode: element.positionMode,
        horizontalOffset: { value: element.centerX, unit: element.centerXUnit },
        verticalOffset: { value: element.centerY, unit: element.centerYUnit },
      },
      animation: element.animation,
      animationDurationMs: element.animationDurationMs,
      animationDelayMs: element.animationDelayMs,
      animationIteration: element.animationIteration,
    };
  if (type === "square" || type === "circle" || type === "line")
    return {
      id: element.id,
      type,
      binding: safeFieldKey(element.key),
      styles: {
        ...commonStyles,
        ...(type === "circle" ? { borderRadius: "50%" } : {}),
      },
      animation: element.animation,
      positioning: {
        mode: element.positionMode,
        horizontalOffset: { value: element.centerX, unit: element.centerXUnit },
        verticalOffset: { value: element.centerY, unit: element.centerYUnit },
      },
      animationDurationMs: element.animationDurationMs,
      animationDelayMs: element.animationDelayMs,
      animationIteration: element.animationIteration,
    };
  if (type === "ticker")
    return {
      id: element.id,
      type,
      binding: safeFieldKey(element.key),
      styles: {
        ...commonStyles,
        color: element.color,
        fontSize: element.fontSize,
        fontWeight: element.fontWeight,
        fontFamily: element.fontFamily,
        fontStyle: element.fontStyle,
        textDecoration: element.textDecoration,
        textTransform: element.textTransform,
        lineHeight: element.lineHeight,
        letterSpacing: element.letterSpacing,
        textAlign: element.textAlign,
        alignItems: element.tickerVerticalAlign,
        padding: `${element.padding}${element.paddingUnit}`,
      },
      ticker: {
        direction: element.tickerDirection,
        speed: element.tickerSpeed,
        separator: element.tickerSeparator.trim() || "◆",
        separatorColor: element.tickerSeparatorColor,
        separatorSpacing: element.tickerSeparatorSpacing,
        sequenceGap: element.tickerSequenceGap,
      },
      animation: element.animation,
      positioning: {
        mode: element.positionMode,
        horizontalOffset: { value: element.centerX, unit: element.centerXUnit },
        verticalOffset: { value: element.centerY, unit: element.centerYUnit },
      },
      animationDurationMs: element.animationDurationMs,
      animationDelayMs: element.animationDelayMs,
      animationIteration: element.animationIteration,
    };
  if (type === "digitalTime" || type === "displayDate")
    return {
      id: element.id,
      type,
      binding: safeFieldKey(element.key),
      styles: {
        ...commonStyles,
        color: element.color,
        fontSize: element.fontSize,
        fontWeight: element.fontWeight,
        fontFamily: element.fontFamily,
        fontStyle: element.fontStyle,
        textDecoration: element.textDecoration,
        textTransform: element.textTransform,
        lineHeight: element.lineHeight,
        letterSpacing: element.letterSpacing,
        textAlign: element.textAlign,
        justifyContent:
          element.textAlign === "center"
            ? "center"
            : element.textAlign === "right"
              ? "flex-end"
              : "flex-start",
        padding: `${element.padding}${element.paddingUnit}`,
      },
      ...(type === "digitalTime"
        ? {
            digitalClock: {
              showSeconds: element.digitalShowSeconds,
              use24Hour: element.digitalUse24Hour,
            },
          }
        : { dateFormat: element.dateFormat.trim() || "dddd, MMMM D, YYYY" }),
      animation: element.animation,
      positioning: {
        mode: element.positionMode,
        horizontalOffset: { value: element.centerX, unit: element.centerXUnit },
        verticalOffset: { value: element.centerY, unit: element.centerYUnit },
      },
      animationDurationMs: element.animationDurationMs,
      animationDelayMs: element.animationDelayMs,
      animationIteration: element.animationIteration,
    };
  if (type === "analogClock")
    return {
      id: element.id,
      type,
      binding: safeFieldKey(element.key),
      styles: {
        ...commonStyles,
        color: element.color,
        padding: `${element.padding}${element.paddingUnit}`,
      },
      analogClock: {
        showHourMarkers: element.analogShowHourMarkers,
        showSecondHand: element.analogShowSecondHand,
        showNumbers: element.analogShowNumbers,
        numberColor: element.analogNumberColor,
        numberFontFamily: element.analogNumberFontFamily,
        numberFontSize: element.analogNumberFontSize,
        numberFontWeight: element.analogNumberFontWeight,
        numberRadius: element.analogNumberRadius,
        markerColor: element.analogMarkerColor,
        hourHandColor: element.analogHourHandColor,
        minuteHandColor: element.analogMinuteHandColor,
        secondHandColor: element.analogSecondHandColor,
        markerLength: element.analogMarkerLength,
        hourHandLength: element.analogHourHandLength,
        minuteHandLength: element.analogMinuteHandLength,
        secondHandLength: element.analogSecondHandLength,
        handThickness: element.analogHandThickness,
      },
      animation: element.animation,
      positioning: {
        mode: element.positionMode,
        horizontalOffset: { value: element.centerX, unit: element.centerXUnit },
        verticalOffset: { value: element.centerY, unit: element.centerYUnit },
      },
      animationDurationMs: element.animationDurationMs,
      animationDelayMs: element.animationDelayMs,
      animationIteration: element.animationIteration,
    };
  if (
    type === "imageCarousel" ||
    type === "textCarousel" ||
    type === "contentCarousel" ||
    type === "wordpressPosts"
  )
    return {
      id: element.id,
      type,
      binding: safeFieldKey(element.key),
      styles: {
        ...commonStyles,
        padding: `${element.padding}${element.paddingUnit}`,
        color: element.color,
      },
      carousel: element.carousel,
      animation: element.animation,
      positioning: {
        mode: element.positionMode,
        horizontalOffset: { value: element.centerX, unit: element.centerXUnit },
        verticalOffset: { value: element.centerY, unit: element.centerYUnit },
      },
      animationDurationMs: element.animationDurationMs,
      animationDelayMs: element.animationDelayMs,
      animationIteration: element.animationIteration,
    };
  return {
    id: element.id,
    type,
    binding: safeFieldKey(element.key),
    styles: {
      ...commonStyles,
      objectFit: element.objectFit,
      objectPosition: element.objectPosition,
      padding: `${element.padding}${element.paddingUnit}`,
      color: element.color,
    },
    animation: element.animation,
    positioning: {
      mode: element.positionMode,
      horizontalOffset: { value: element.centerX, unit: element.centerXUnit },
      verticalOffset: { value: element.centerY, unit: element.centerYUnit },
    },
    animationDurationMs: element.animationDurationMs,
    animationDelayMs: element.animationDelayMs,
    animationIteration: element.animationIteration,
  };
}

function sampleValues(elements: BuilderElement[]): SlideFieldValues {
  const carouselTextItems = (previewText: string) =>
    previewText
      .split(/\r?\n/)
      .map((line) => {
        const [title = "", ...description] = line.split("|");
        return { title: title.trim(), description: description.join("|").trim() };
      })
      .filter((item) => item.title);
  return Object.fromEntries(
    elements.map((element) => [
      safeFieldKey(element.key),
      element.type === "video"
        ? sampleVideo
        : element.type === "youtube"
          ? sampleYoutube
          : element.type === "youtubeList"
            ? { urls: [sampleYoutube], shuffle: true }
            : element.type === "html"
              ? element.htmlContent
              : element.type === "image" || element.type === "logo"
                ? sampleImage
                : element.type === "imageCarousel"
                  ? [sampleImage, sampleImage, sampleImage]
                  : element.type === "wordpressPosts"
                    ? {
                        mode: "wordpress",
                        apiUrl: "https://example.com/wp-json",
                        postType: "posts",
                        limit: 10,
                        posts: carouselTextItems(element.previewText).map((item, index) => ({
                          ...item,
                          url: `https://example.com/posts/${index + 1}`,
                        })),
                      }
                    : element.type === "textCarousel"
                      ? carouselTextItems(element.previewText)
                      : element.type === "contentCarousel"
                        ? carouselTextItems(element.previewText).map((item) => ({
                            ...item,
                            image: sampleImage,
                          }))
                        : element.type === "qrCall"
                          ? "+974 5555 1234"
                          : element.type === "qrUrl"
                            ? "https://example.com/service"
                            : element.type === "qrWhatsApp"
                              ? {
                                  phone: "+97455551234",
                                  message: "Hello, I would like more information.",
                                }
                              : element.type === "qrVCard"
                                ? {
                                    fullName: "Q-Assist Contact",
                                    organization: "Q-Assist",
                                    title: "Customer service",
                                    phone: "+97455551234",
                                    email: "hello@example.com",
                                    website: "https://example.com",
                                  }
                                : ["digitalTime", "displayDate", "analogClock"].includes(
                                      element.type,
                                    )
                                  ? ""
                                  : element.type === "ticker"
                                    ? { mode: "manual", headlines: element.previewText }
                                    : element.previewText,
    ]),
  );
}

function carouselPreviewItemCount(previewText: string): number {
  return previewText
    .split(/\r?\n/)
    .map((line) => line.split("|")[0]?.trim() ?? "")
    .filter(Boolean).length;
}

function arranged(elements: BuilderElement[], preset: ArrangePreset): BuilderElement[] {
  if (preset === "free") return elements;
  const media = elements.filter((element) =>
    [
      "html",
      "image",
      "logo",
      "video",
      "youtube",
      "youtubeList",
      "qrCall",
      "qrUrl",
      "qrWhatsApp",
      "imageCarousel",
      "textCarousel",
      "contentCarousel",
    ].includes(element.type),
  );
  const text = elements.filter((element) => ["text", "textarea"].includes(element.type));
  if (preset === "centered")
    return elements.map((element, index) => ({
      ...element,
      positionMode: "free",
      x: element.type === "logo" ? 35 : 15,
      y: 8 + index * Math.min(20, 74 / Math.max(1, elements.length)),
      right: null,
      bottom: null,
      width: element.type === "logo" ? 30 : 70,
      height: [
        "html",
        "image",
        "video",
        "youtube",
        "youtubeList",
        "imageCarousel",
        "textCarousel",
        "contentCarousel",
      ].includes(element.type)
        ? 34
        : element.type === "logo"
          ? 18
          : 15,
      xUnit: "%",
      yUnit: "%",
      widthUnit: "%",
      heightUnit: "%",
      textAlign: "center",
    }));
  if (preset === "hero" && media[0]) {
    return elements.map((element, index) =>
      element.id === media[0]!.id
        ? {
            ...element,
            positionMode: "free",
            x: 0,
            y: 0,
            right: null,
            bottom: null,
            width: 100,
            height: 100,
            xUnit: "%",
            yUnit: "%",
            widthUnit: "%",
            heightUnit: "%",
            zIndex: 1,
            borderRadius: 0,
          }
        : {
            ...element,
            positionMode: "free",
            x: 10,
            y: 18 + index * 18,
            right: null,
            bottom: null,
            width: 80,
            height: element.type === "textarea" ? 24 : 16,
            xUnit: "%",
            yUnit: "%",
            widthUnit: "%",
            heightUnit: "%",
            zIndex: index + 5,
            textAlign: "center",
            backgroundColor: "rgba(0,0,0,0.42)",
          },
    );
  }
  const mediaLeft = preset === "split-left";
  return elements.map((element) => {
    const isMedia = media.some((item) => item.id === element.id);
    const localIndex = isMedia
      ? media.findIndex((item) => item.id === element.id)
      : text.findIndex((item) => item.id === element.id);
    return {
      ...element,
      positionMode: "free",
      x: isMedia ? (mediaLeft ? 4 : 52) : mediaLeft ? 54 : 4,
      y: 6 + Math.max(0, localIndex) * (isMedia ? 42 : 21),
      right: null,
      bottom: null,
      width: 44,
      height: isMedia ? Math.min(82, 38) : element.type === "textarea" ? 25 : 16,
      xUnit: "%",
      yUnit: "%",
      widthUnit: "%",
      heightUnit: "%",
      textAlign: isMedia ? element.textAlign : "left",
    };
  });
}

function ElementIcon({ type }: { type: BuilderElementType }) {
  if (type === "html") return <Code2 size={15} />;
  if (type === "square") return <Square size={15} />;
  if (type === "circle") return <Circle size={15} />;
  if (type === "line") return <Minus size={15} />;
  if (type === "label" || type === "verticalLabel") return <Tag size={15} />;
  if (type === "qrCall") return <Phone size={15} />;
  if (type === "qrUrl" || type === "wordpressPostQr") return <Globe2 size={15} />;
  if (type === "qrWhatsApp") return <MessageCircle size={15} />;
  if (type === "qrVCard") return <ContactRound size={15} />;
  if (type === "displayDate") return <CalendarDays size={15} />;
  if (type === "digitalTime" || type === "analogClock") return <Clock3 size={15} />;
  if (type === "image" || type === "logo" || type === "staticImage") return <Image size={15} />;
  if (["imageCarousel", "textCarousel", "contentCarousel", "wordpressPosts"].includes(type))
    return <GalleryVerticalEnd size={15} />;
  if (type === "video" || type === "youtube" || type === "youtubeList") return <Video size={15} />;
  return <Type size={15} />;
}

export function TemplateGuiBuilder({
  initialDefinition,
  mode,
  clientOptions = [],
  saving,
  error,
  onCancel,
  onSave,
}: TemplateGuiBuilderProps) {
  const isCreating = mode === "create";
  const [name, setName] = useState(initialDefinition.name);
  const [key, setKey] = useState(initialDefinition.key);
  const [description, setDescription] = useState(initialDefinition.description);
  const [status, setStatus] = useState<"draft" | "published">(
    initialDefinition.status === "published" ? "published" : "draft",
  );
  const [purpose, setPurpose] = useState<"slide" | "overlay">(initialDefinition.purpose ?? "slide");
  const [availabilityMode, setAvailabilityMode] = useState<"global" | "clients">(
    initialDefinition.availability.global ? "global" : "clients",
  );
  const [availableClientIds, setAvailableClientIds] = useState(
    initialDefinition.availability.clientIds,
  );
  const [backgroundColor, setBackgroundColor] = useState(initialDefinition.canvas.backgroundColor);
  const [elements, setElements] = useState<BuilderElement[]>(() =>
    initialElements(initialDefinition),
  );
  const [clipboardElement, setClipboardElement] = useState<BuilderElement | null>(() =>
    builderElementClipboard ? cloneBuilderElement(builderElementClipboard) : null,
  );
  const [clipboardStatus, setClipboardStatus] = useState("");
  const [uploadingStaticImage, setUploadingStaticImage] = useState(false);
  const [selectedId, setSelectedId] = useState(elements[0]?.id ?? "");
  const [previewHeight, setPreviewHeight] = useState(430);
  const [previewScale, setPreviewScale] = useState(1);
  const [previewZoomPercent, setPreviewZoomPercent] = useState(100);
  const [previewPresetId, setPreviewPresetId] =
    useState<(typeof previewScreenPresets)[number]["id"]>("full-hd");
  const previewRef = useRef<HTMLDivElement>(null);
  const selected = elements.find((element) => element.id === selectedId);
  const [aspectWidth, aspectHeight] = initialDefinition.canvas.aspectRatio.split(":").map(Number);
  const previewScreenHeight = Math.round(
    previewScreenWidth * ((aspectHeight || 9) / (aspectWidth || 16)),
  );
  const previewPreset =
    previewScreenPresets.find((candidate) => candidate.id === previewPresetId) ??
    previewScreenPresets[2];
  const previewCanvasWidth = previewPreset.width;
  const previewCanvasHeight = previewPreset.height;

  useEffect(() => {
    const preview = previewRef.current;
    if (!preview) return;
    const updateScale = (): void => setPreviewScale(preview.clientWidth / previewCanvasWidth);
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(preview);
    return () => observer.disconnect();
  }, [previewCanvasWidth]);

  const definition = useMemo<SlideTypeDefinition>(
    () => ({
      key: safeKey(key),
      name: name.trim() || "Untitled slide type",
      description: description.trim(),
      version: initialDefinition.version,
      status,
      purpose,
      canvas: {
        ...initialDefinition.canvas,
        width: previewScreenWidth,
        height: previewScreenHeight,
        backgroundColor,
      },
      fields: elements.map(fieldDefinition),
      layout: {
        type: "container",
        styles: {
          position: "relative",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        },
        children: elements.map((element) =>
          elementNode(element, previewScreenWidth, previewScreenHeight),
        ),
      },
      defaultStyles: {},
      allowedStyleOverrides: [
        "left",
        "top",
        "right",
        "bottom",
        "width",
        "height",
        "color",
        "backgroundColor",
        "fontFamily",
        "fontSize",
        "fontWeight",
        "textAlign",
        "opacity",
        "borderRadius",
        "padding",
        "objectFit",
        "objectPosition",
        "borderWidth",
        "borderStyle",
        "borderColor",
        "fontStyle",
        "lineHeight",
        "letterSpacing",
        "textDecoration",
        "textTransform",
        "whiteSpace",
        "overflow",
        "zIndex",
      ],
      animations: {
        entrance: { name: "fade-in", durationMs: 500, delayMs: 0 },
        exit: { name: "fade-out", durationMs: 300, delayMs: 0 },
      },
      availability: {
        global: availabilityMode === "global",
        clientIds: availabilityMode === "clients" ? availableClientIds : [],
      },
    }),
    [
      backgroundColor,
      description,
      elements,
      initialDefinition.version,
      availabilityMode,
      availableClientIds,
      key,
      name,
      previewScreenHeight,
      purpose,
      status,
    ],
  );
  const parsed = slideTypeDefinitionSchema.safeParse(definition);
  const previewDefinition = useMemo<SlideTypeDefinition>(
    () => ({
      ...definition,
      canvas: {
        ...definition.canvas,
        width: previewCanvasWidth,
        height: previewCanvasHeight,
        aspectRatio: `${previewCanvasWidth}:${previewCanvasHeight}`,
      },
      layout: {
        ...definition.layout,
        children: elements.map((element) =>
          elementNode(element, previewCanvasWidth, previewCanvasHeight),
        ),
      },
    }),
    [definition, elements, previewCanvasHeight, previewCanvasWidth],
  );
  const duplicateKey = elements.some(
    (element, index) =>
      elements.findIndex(
        (candidate) => safeFieldKey(candidate.key) === safeFieldKey(element.key),
      ) !== index,
  );
  const firstGeometryIssue =
    elements
      .map((element) => geometryIssue(element, previewScreenWidth, previewScreenHeight))
      .find((issue) => issue !== null) ?? null;
  const valid = parsed.success && !duplicateKey && !firstGeometryIssue && elements.length > 0;

  function updateElement(id: string, patch: Partial<BuilderElement>): void {
    const geometryValue = (
      patchValue: number | null | undefined,
      currentValue: number | null,
      minimum: number,
    ): number | null =>
      patchValue === undefined
        ? currentValue
        : patchValue === null
          ? null
          : clamp(patchValue, minimum, 7680);
    setElements((current) =>
      current.map((element) =>
        element.id === id
          ? {
              ...element,
              ...patch,
              x: geometryValue(patch.x, element.x, 0),
              y: geometryValue(patch.y, element.y, 0),
              right: geometryValue(patch.right, element.right, 0),
              bottom: geometryValue(patch.bottom, element.bottom, 0),
              width: geometryValue(patch.width, element.width, 0.5),
              height: geometryValue(patch.height, element.height, 0.5),
            }
          : patch.key !== undefined &&
              element.wordpressSourceBinding === current.find((item) => item.id === id)?.key
            ? { ...element, wordpressSourceBinding: patch.key }
            : element,
      ),
    );
  }

  function updateCarousel(id: string, patch: Partial<CarouselSettings>): void {
    setElements((current) =>
      current.map((element) =>
        element.id === id ? { ...element, carousel: { ...element.carousel, ...patch } } : element,
      ),
    );
  }

  function updateCarouselText(
    id: string,
    target: "title" | "description",
    patch: Partial<CarouselSettings["title"]>,
  ): void {
    setElements((current) =>
      current.map((element) =>
        element.id === id
          ? {
              ...element,
              carousel: {
                ...element.carousel,
                [target]: { ...element.carousel[target], ...patch },
              },
            }
          : element,
      ),
    );
  }

  function addElement(type: BuilderElementType): void {
    if (elements.length >= 80) return;
    const next = elements.length + 1;
    const typeLabel: Record<BuilderElementType, string> = {
      text: "Text",
      label: "Label",
      verticalLabel: "Vertical label",
      square: "Square",
      circle: "Circle",
      line: "Line",
      textarea: "Long text",
      html: "HTML",
      image: "Image",
      logo: "Logo",
      video: "Video",
      youtube: "YouTube",
      youtubeList: "YouTube List",
      digitalTime: "Digital time",
      displayDate: "Date",
      analogClock: "Analog clock",
      qrCall: "Call number QR",
      qrUrl: "Browse URL QR",
      qrWhatsApp: "WhatsApp message QR",
      qrVCard: "VCF contact QR",
      ticker: "News ticker",
      imageCarousel: "Image carousel",
      textCarousel: "Title and description carousel",
      contentCarousel: "Image, title, and description carousel",
      wordpressPosts: "WordPress posts",
      wordpressPostQr: "WordPress post QR",
      staticImage: "Static image",
    };
    const qrElement =
      type === "qrCall" ||
      type === "qrUrl" ||
      type === "qrWhatsApp" ||
      type === "qrVCard" ||
      type === "wordpressPostQr";
    const element: BuilderElement = {
      id: crypto.randomUUID(),
      key: `${type}_${next}`,
      label: `${typeLabel[type]} ${next}`,
      type,
      required: false,
      previewText:
        type === "label" || type === "verticalLabel"
          ? type === "verticalLabel"
            ? "Vertical label"
            : "Label"
          : type === "textarea"
            ? "A clear supporting message that stays inside its editable box."
            : type === "ticker"
              ? "First news headline\nSecond news headline\nThird news headline"
              : type === "textCarousel" || type === "contentCarousel" || type === "wordpressPosts"
                ? "Welcome | A clear supporting description\nExplore | A second carousel item\nConnect | A final call to action"
                : `${typeLabel[type]} sample`,
      htmlContent: type === "html" ? defaultHtmlDocument : "",
      x: 10 + (next % 4) * 5,
      y: 10 + (next % 5) * 8,
      right: null,
      bottom: null,
      width:
        type === "line"
          ? 40
          : type === "square" || type === "circle" || type === "wordpressPostQr"
            ? 220
            : type === "text" ||
                type === "label" ||
                type === "textarea" ||
                type === "digitalTime" ||
                type === "displayDate"
              ? 46
              : type === "ticker"
                ? 80
                : type.endsWith("Carousel") || type === "wordpressPosts"
                  ? 70
                  : type === "html"
                    ? 70
                    : 38,
      height:
        type === "line"
          ? 4
          : type === "square" || type === "circle" || type === "wordpressPostQr"
            ? 220
            : type === "text" || type === "label"
              ? 16
              : type === "textarea"
                ? 25
                : type === "ticker"
                  ? 10
                  : type.endsWith("Carousel") || type === "wordpressPosts"
                    ? 48
                    : type === "html"
                      ? 50
                      : type === "analogClock"
                        ? 30
                        : 36,
      xUnit: "%",
      yUnit: "%",
      rightUnit: "%",
      bottomUnit: "%",
      widthUnit: type === "square" || type === "circle" || type === "wordpressPostQr" ? "px" : "%",
      heightUnit:
        type === "line" || type === "square" || type === "circle" || type === "wordpressPostQr"
          ? "px"
          : "%",
      positionMode: "free",
      centerX: 0,
      centerY: 0,
      centerXUnit: "%",
      centerYUnit: "%",
      zIndex: next,
      color: qrElement ? "#111111" : "#ffffff",
      backgroundColor:
        qrElement || type === "square" || type === "circle" || type === "line"
          ? "#ffffff"
          : "transparent",
      fontSize: type === "textarea" ? 34 : type === "ticker" ? 28 : 56,
      fontWeight: type === "textarea" ? 400 : 700,
      textAlign: "left",
      opacity: 1,
      borderRadius: type === "image" || type === "video" ? 24 : type === "analogClock" ? 200 : 0,
      padding: type === "text" || type === "textarea" || type === "ticker" || qrElement ? 12 : 0,
      paddingUnit: "px",
      objectFit: type === "logo" || type === "staticImage" ? "contain" : "cover",
      objectPosition: "center",
      borderWidth: type === "analogClock" ? 4 : 0,
      borderColor: "#ffffff",
      fontFamily: "Inter, sans-serif",
      fontStyle: "normal",
      textDecoration: "none",
      textTransform: "none",
      lineHeight: 1.2,
      letterSpacing: 0,
      textFit: "fixed",
      textWrap: type === "text" ? "preserve" : "wrap",
      minFontSize: 12,
      tickerDirection: "left",
      tickerVerticalAlign: "center",
      tickerSpeed: 80,
      tickerSeparator: "◆",
      tickerSeparatorColor: "#ffffff",
      tickerSeparatorSpacing: 24,
      tickerSequenceGap: 80,
      digitalShowSeconds: true,
      digitalUse24Hour: false,
      dateFormat: "dddd, MMMM D, YYYY",
      analogShowHourMarkers: true,
      analogShowSecondHand: true,
      analogShowNumbers: false,
      analogNumberColor: "#ffffff",
      analogNumberFontFamily: "Inter, sans-serif",
      analogNumberFontSize: 24,
      analogNumberFontWeight: 600,
      analogNumberRadius: 36,
      analogMarkerColor: "#ffffff",
      analogHourHandColor: "#ffffff",
      analogMinuteHandColor: "#ffffff",
      analogSecondHandColor: "#ef4444",
      analogMarkerLength: 8,
      analogHourHandLength: 25,
      analogMinuteHandLength: 34,
      analogSecondHandLength: 38,
      analogHandThickness: 2.8,
      carousel: {
        ...defaultCarouselSettings,
        title: { ...defaultCarouselSettings.title },
        description: { ...defaultCarouselSettings.description },
      },
      wordpressSourceBinding: elements.find((item) => item.type === "wordpressPosts")?.key ?? "",
      staticImageAssetId: "",
      verticalLabelOrientation: "clockwise",
      animation: "none",
      animationDurationMs: 500,
      animationDelayMs: 0,
      animationIteration: "once",
    };
    if (type === "verticalLabel") {
      element.width = 100;
      element.height = 480;
      element.widthUnit = "px";
      element.heightUnit = "px";
      element.textAlign = "center";
      element.textFit = "shrink";
      element.textWrap = "nowrap";
    }
    setElements((current) => [...current, element]);
    setSelectedId(element.id);
  }

  function addBackgroundImage(): void {
    addElement("image");
    setElements((current) => {
      const last = current.at(-1);
      return last
        ? current.map((element) =>
            element.id === last.id
              ? {
                  ...element,
                  key: "backgroundImage",
                  label: "Background image",
                  positionMode: "free",
                  x: 0,
                  y: 0,
                  right: null,
                  bottom: null,
                  width: 100,
                  height: 100,
                  xUnit: "%",
                  yUnit: "%",
                  widthUnit: "%",
                  heightUnit: "%",
                  zIndex: 1,
                  borderRadius: 0,
                  objectFit: "cover",
                }
              : { ...element, zIndex: Math.max(2, element.zIndex + 1) },
          )
        : current;
    });
  }

  function moveLayer(index: number, direction: -1 | 1): void {
    const target = index + direction;
    if (target < 0 || target >= elements.length) return;
    const next = [...elements];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setElements(next.map((element, position) => ({ ...element, zIndex: position + 1 })));
  }

  function copiedElement(
    source: BuilderElement,
    currentElements: BuilderElement[],
    id = crypto.randomUUID(),
  ): BuilderElement {
    const geometry = resolvedGeometry(source, previewCanvasWidth, previewCanvasHeight);
    const position = geometry
      ? repositionPatch(
          source,
          clamp(geometry.left + 3, 0, 100 - geometry.width),
          clamp(geometry.top + 3, 0, 100 - geometry.height),
          previewCanvasWidth,
          previewCanvasHeight,
        )
      : {};
    return {
      ...cloneBuilderElement(source),
      ...position,
      id,
      key: nextElementCopyKey(source.key, currentElements),
      label: nextElementCopyLabel(source.label, currentElements),
      zIndex: currentElements.length + 1,
    };
  }

  function copySelectedElement(): void {
    if (!selected) return;
    const snapshot = cloneBuilderElement(selected);
    builderElementClipboard = snapshot;
    setClipboardElement(cloneBuilderElement(snapshot));
    setClipboardStatus(`Copied ${selected.label}. Paste is ready.`);
  }

  function pasteCopiedElement(): void {
    if (!clipboardElement || elements.length >= 80) return;
    const copyId = crypto.randomUUID();
    setElements((current) =>
      current.length >= 80
        ? current
        : [...current, copiedElement(clipboardElement, current, copyId)],
    );
    setSelectedId(copyId);
    setClipboardStatus(`Pasted ${clipboardElement.label}.`);
  }

  function startDrag(event: ReactPointerEvent<HTMLButtonElement>, element: BuilderElement): void {
    event.preventDefault();
    setSelectedId(element.id);
    const canvas = event.currentTarget.closest<HTMLElement>(".design-canvas");
    if (!canvas) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const origin = resolvedGeometry(element, previewCanvasWidth, previewCanvasHeight);
    if (!origin) return;
    const renderedWidth = previewCanvasWidth * previewScale * (previewZoomPercent / 100);
    const renderedHeight = previewCanvasHeight * previewScale * (previewZoomPercent / 100);
    const move = (pointer: PointerEvent): void => {
      const left = clamp(
        origin.left + ((pointer.clientX - startX) / renderedWidth) * 100,
        0,
        100 - origin.width,
      );
      const top = clamp(
        origin.top + ((pointer.clientY - startY) / renderedHeight) * 100,
        0,
        100 - origin.height,
      );
      updateElement(
        element.id,
        repositionPatch(element, left, top, previewCanvasWidth, previewCanvasHeight),
      );
    };
    const stop = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  }

  function applyResize(
    element: BuilderElement,
    handle: ResizeHandle,
    dx: number,
    dy: number,
  ): void {
    const origin = resolvedGeometry(element, previewCanvasWidth, previewCanvasHeight);
    if (!origin) return;
    updateElement(
      element.id,
      resizePatch(
        element,
        resizedGeometry(origin, handle, dx, dy),
        previewCanvasWidth,
        previewCanvasHeight,
      ),
    );
  }

  function startElementResize(
    event: ReactPointerEvent<HTMLButtonElement>,
    element: BuilderElement,
    handle: ResizeHandle,
  ): void {
    event.preventDefault();
    event.stopPropagation();
    setSelectedId(element.id);
    const startX = event.clientX;
    const startY = event.clientY;
    const origin = resolvedGeometry(element, previewCanvasWidth, previewCanvasHeight);
    if (!origin) return;
    const renderedWidth = previewCanvasWidth * previewScale * (previewZoomPercent / 100);
    const renderedHeight = previewCanvasHeight * previewScale * (previewZoomPercent / 100);
    const move = (pointer: PointerEvent): void => {
      const next = resizedGeometry(
        origin,
        handle,
        ((pointer.clientX - startX) / renderedWidth) * 100,
        ((pointer.clientY - startY) / renderedHeight) * 100,
      );
      updateElement(
        element.id,
        resizePatch(element, next, previewCanvasWidth, previewCanvasHeight),
      );
    };
    const stop = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  }

  function resizeWithKeyboard(
    event: ReactKeyboardEvent<HTMLButtonElement>,
    element: BuilderElement,
    handle: ResizeHandle,
  ): void {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 5 : 1;
    const dx = event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0;
    const dy = event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;
    applyResize(element, handle, dx, dy);
  }

  function startPreviewResize(event: ReactPointerEvent<HTMLDivElement>): void {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = previewHeight;
    const move = (pointer: PointerEvent): void =>
      setPreviewHeight(clamp(startHeight + pointer.clientY - startY, 240, 720));
    const stop = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  }

  return (
    <div
      className="visual-builder visual-builder--integrated"
      style={{ "--builder-preview-height": `${previewHeight}px` } as CSSProperties}
    >
      <section className="builder-identity builder-panel">
        <div>
          <span className="eyebrow">Slide type design</span>
          <h3>Identity, canvas, layout, and elements</h3>
        </div>
        <div className="form-grid form-grid--four">
          <label>
            <span>Name</span>
            <input
              value={name}
              maxLength={120}
              onChange={(event) => {
                setName(event.target.value);
                if (isCreating) setKey(safeKey(event.target.value));
              }}
              required
            />
          </label>
          <label>
            <span>Stable key</span>
            <input
              value={key}
              onChange={(event) => setKey(safeKey(event.target.value))}
              disabled={!isCreating}
              required
            />
            <small>
              {isCreating
                ? "Generated from the name; you may adjust it before creation."
                : "Locked after creation so existing versions and assigned slides keep the same identity."}
            </small>
          </label>
          <label>
            <span>Canvas color</span>
            <ColorPicker value={backgroundColor} onChange={setBackgroundColor} />
          </label>
          <label>
            <span>Save status</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as "draft" | "published")}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </label>
          <label>
            <span>Template purpose</span>
            <select
              value={purpose}
              onChange={(event) => setPurpose(event.target.value as "slide" | "overlay")}
            >
              <option value="slide">Rotating slide</option>
              <option value="overlay">Playlist overlay</option>
            </select>
          </label>
        </div>
        <label>
          <span>Description</span>
          <textarea
            value={description}
            maxLength={500}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <fieldset className="dynamic-field-group">
          <legend>Client availability</legend>
          <label>
            <span>Availability scope</span>
            <select
              aria-label="Client availability"
              value={availabilityMode}
              onChange={(event) => {
                const mode = event.target.value as "global" | "clients";
                setAvailabilityMode(mode);
                if (mode === "global") setAvailableClientIds([]);
              }}
            >
              <option value="global">All clients</option>
              <option value="clients">Selected clients</option>
            </select>
          </label>
          {availabilityMode === "clients" ? (
            <div className="choice-list availability-client-list">
              {clientOptions.length ? (
                clientOptions.map((client) => (
                  <label key={client.id}>
                    <input
                      type="checkbox"
                      checked={availableClientIds.includes(client.id)}
                      onChange={(event) =>
                        setAvailableClientIds((current) =>
                          event.target.checked
                            ? [...new Set([...current, client.id])]
                            : current.filter((id) => id !== client.id),
                        )
                      }
                    />
                    <span>{client.name}</span>
                  </label>
                ))
              ) : (
                <p className="muted">No active clients are available.</p>
              )}
            </div>
          ) : null}
        </fieldset>
      </section>

      <div className="design-toolbar" role="toolbar" aria-label="Slide element tools">
        <span>Edit selected</span>
        <button
          type="button"
          className="button button--secondary"
          disabled={!selected}
          onClick={copySelectedElement}
          aria-label="Copy selected element"
        >
          <ClipboardCopy size={16} /> Copy
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={!clipboardElement || elements.length >= 80}
          onClick={pasteCopiedElement}
          aria-label="Paste copied element"
          title={clipboardElement ? `Paste ${clipboardElement.label}` : "Copy an element first"}
        >
          <ClipboardPaste size={16} /> Paste
        </button>
        <span>Add bounded element</span>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("label")}
        >
          <Tag size={16} /> Label
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("verticalLabel")}
        >
          <Tag size={16} /> Vertical label
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("square")}
        >
          <Square size={16} /> Square
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("circle")}
        >
          <Circle size={16} /> Circle
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("line")}
        >
          <Minus size={16} /> Line
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("text")}
        >
          <Type size={16} /> Text
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("textarea")}
        >
          <AlignCenter size={16} /> Long text
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("html")}
        >
          <Code2 size={16} /> HTML
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("digitalTime")}
        >
          <Clock3 size={16} /> Digital time
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("displayDate")}
        >
          <CalendarDays size={16} /> Date
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("analogClock")}
        >
          <Clock3 size={16} /> Analog clock
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("image")}
        >
          <Image size={16} /> Image
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("staticImage")}
        >
          <Image size={16} /> Static image
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("imageCarousel")}
        >
          <GalleryVerticalEnd size={16} /> Image carousel
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("textCarousel")}
        >
          <GalleryVerticalEnd size={16} /> Title + description carousel
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("contentCarousel")}
        >
          <GalleryVerticalEnd size={16} /> Content card carousel
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("wordpressPosts")}
        >
          <Globe2 size={16} /> WordPress posts
        </button>
        <button
          type="button"
          className="button button--secondary"
          onClick={() => addElement("wordpressPostQr")}
          disabled={elements.length >= 80}
        >
          <Globe2 size={16} /> WordPress post QR
        </button>

        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("logo")}
        >
          <Image size={16} /> Logo
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("ticker")}
        >
          <Type size={16} /> News ticker
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("video")}
        >
          <Video size={16} /> Video
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("youtube")}
        >
          <Video size={16} /> YouTube
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("youtubeList")}
        >
          <Video size={16} /> YouTube List
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("qrCall")}
        >
          <Phone size={16} /> Call number QR
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("qrUrl")}
        >
          <Globe2 size={16} /> Browse URL QR
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("qrWhatsApp")}
        >
          <MessageCircle size={16} /> WhatsApp QR
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={() => addElement("qrVCard")}
        >
          <ContactRound size={16} /> VCF contact QR
        </button>
        <button
          type="button"
          className="button button--secondary"
          disabled={elements.length >= 80}
          onClick={addBackgroundImage}
        >
          <Image size={16} /> Background image
        </button>
        <label className="toolbar-select">
          <span>Arrange</span>
          <select
            aria-label="Arrange elements"
            defaultValue="free"
            onChange={(event) =>
              setElements((current) => arranged(current, event.target.value as ArrangePreset))
            }
          >
            <option value="free">Free positioning</option>
            <option value="centered">Centered stack</option>
            <option value="split-left">Media left</option>
            <option value="split-right">Media right</option>
            <option value="hero">Full-screen hero</option>
          </select>
        </label>
        <span className="builder-clipboard-status" role="status" aria-live="polite">
          {clipboardStatus}
        </span>
      </div>

      <div className="design-workspace">
        <section className="design-stage">
          <header>
            <div>
              <span className="eyebrow">Live player canvas</span>
              <p>Drag an element to reposition it. Every element is clipped to its own box.</p>
            </div>
            <div className="preview-stage-controls">
              <label className="preview-screen-control">
                <span>Preview screen size</span>
                <select
                  aria-label="Preview screen size"
                  value={previewPreset.id}
                  onChange={(event) =>
                    setPreviewPresetId(
                      event.target.value as (typeof previewScreenPresets)[number]["id"],
                    )
                  }
                >
                  {previewScreenPresets.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.label} · {candidate.width} × {candidate.height}
                    </option>
                  ))}
                </select>
              </label>
              <div className="preview-zoom-control" role="group" aria-label="Preview zoom">
                <span>Zoom</span>
                <button
                  type="button"
                  aria-label="Zoom out preview"
                  disabled={previewZoomPercent <= 50}
                  onClick={() => setPreviewZoomPercent((current) => clamp(current - 25, 50, 200))}
                >
                  <Minus size={15} />
                </button>
                <output aria-live="polite">{previewZoomPercent}%</output>
                <button
                  type="button"
                  aria-label="Zoom in preview"
                  disabled={previewZoomPercent >= 200}
                  onClick={() => setPreviewZoomPercent((current) => clamp(current + 25, 50, 200))}
                >
                  <Plus size={15} />
                </button>
                <button
                  type="button"
                  className="preview-zoom-reset"
                  aria-label="Fit preview"
                  disabled={previewZoomPercent === 100}
                  onClick={() => setPreviewZoomPercent(100)}
                >
                  Fit
                </button>
              </div>
              <small>
                Preview only; the saved {previewScreenWidth} × {previewScreenHeight} virtual screen
                is unchanged.
              </small>
            </div>
          </header>
          <div
            ref={previewRef}
            className={`design-canvas ${previewZoomPercent > 100 ? "is-zoomed" : ""}`}
            style={{
              backgroundColor,
              aspectRatio: `${previewCanvasWidth} / ${previewCanvasHeight}`,
              width: `min(100%, ${Math.round(
                previewHeight * (previewCanvasWidth / previewCanvasHeight),
              )}px)`,
            }}
          >
            <div
              className="design-canvas__zoom-surface"
              style={{
                width: `${previewZoomPercent}%`,
                height: `${previewZoomPercent}%`,
              }}
            >
              <div
                className="design-canvas__scaled"
                style={{
                  width: previewCanvasWidth,
                  height: previewCanvasHeight,
                  transform: `scale(${previewScale * (previewZoomPercent / 100)})`,
                }}
              >
                {elements.map((element) => (
                  <div
                    key={element.id}
                    className={`design-element ${selectedId === element.id ? "is-selected" : ""}`}
                    style={{
                      ...geometryStyles(element, previewCanvasWidth, previewCanvasHeight),
                      ...Object.fromEntries(
                        Object.entries(widgetSpacingStyles(elementSpacing(element))).filter(
                          ([key]) => key.startsWith("margin"),
                        ),
                      ),
                      zIndex: element.zIndex,
                      borderRadius: element.borderRadius,
                    }}
                  >
                    <button
                      type="button"
                      className="design-element__move"
                      onPointerDown={(event) => startDrag(event, element)}
                      aria-label={`Select and move ${element.label}`}
                    >
                      <span>
                        <MousePointer2 size={13} />
                        {element.label}
                      </span>
                    </button>
                    {selectedId === element.id
                      ? (["n", "ne", "e", "se", "s", "sw", "w", "nw"] as ResizeHandle[]).map(
                          (handle) => (
                            <button
                              type="button"
                              key={handle}
                              className={`design-element__resize design-element__resize--${handle}`}
                              aria-label={`Resize ${element.label} from ${resizeHandleLabels[handle]}`}
                              onPointerDown={(event) => startElementResize(event, element, handle)}
                              onKeyDown={(event) => resizeWithKeyboard(event, element, handle)}
                            />
                          ),
                        )
                      : null}
                  </div>
                ))}
                {parsed.success ? (
                  <SlideRenderer definition={previewDefinition} values={sampleValues(elements)} />
                ) : null}
              </div>
            </div>
          </div>
          <div
            className="preview-resize-handle"
            role="separator"
            tabIndex={0}
            aria-label="Resize slide preview vertically"
            aria-orientation="horizontal"
            aria-valuemin={240}
            aria-valuemax={720}
            aria-valuenow={previewHeight}
            onPointerDown={startPreviewResize}
            onKeyDown={(event) => {
              if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
              event.preventDefault();
              setPreviewHeight((current) =>
                clamp(current + (event.key === "ArrowUp" ? -20 : 20), 240, 720),
              );
            }}
          >
            <GripHorizontal size={22} />
            <span>Drag or use arrow keys to resize preview</span>
          </div>
        </section>

        <aside className="element-inspector">
          <div className="element-list">
            <header>
              <span className="eyebrow">Elements</span>
              <span>{elements.length}/80</span>
            </header>
            {elements.map((element, index) => (
              <button
                type="button"
                key={element.id}
                className={selectedId === element.id ? "is-selected" : ""}
                onClick={() => setSelectedId(element.id)}
              >
                <ElementIcon type={element.type} />
                <span>{element.label}</span>
                <small>{element.type}</small>
                <span className="layer-actions">
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Move ${element.label} backward`}
                    onClick={(event) => {
                      event.stopPropagation();
                      moveLayer(index, -1);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") moveLayer(index, -1);
                    }}
                  >
                    <ArrowDown size={13} />
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Move ${element.label} forward`}
                    onClick={(event) => {
                      event.stopPropagation();
                      moveLayer(index, 1);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") moveLayer(index, 1);
                    }}
                  >
                    <ArrowUp size={13} />
                  </span>
                </span>
              </button>
            ))}
          </div>

          {selected ? (
            <section className="builder-panel element-properties">
              <header>
                <div>
                  <span className="eyebrow">Selected box</span>
                  <h3>{selected.label}</h3>
                </div>
                <div>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`Duplicate ${selected.label}`}
                    onClick={() => {
                      if (elements.length >= 80) return;
                      const copyId = crypto.randomUUID();
                      setElements((current) =>
                        current.length >= 80
                          ? current
                          : [...current, copiedElement(selected, current, copyId)],
                      );
                      setSelectedId(copyId);
                    }}
                    disabled={elements.length >= 80}
                  >
                    <Copy size={15} />
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button--danger"
                    aria-label={`Remove ${selected.label}`}
                    onClick={() => {
                      setElements((current) =>
                        current.filter((element) => element.id !== selected.id),
                      );
                      setSelectedId(
                        elements.find((element) => element.id !== selected.id)?.id ?? "",
                      );
                    }}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </header>
              <div className="form-grid">
                <label>
                  <span>Label</span>
                  <input
                    value={selected.label}
                    onChange={(event) => updateElement(selected.id, { label: event.target.value })}
                  />
                </label>
                <label>
                  <span>Binding key</span>
                  <input
                    value={selected.key}
                    onChange={(event) =>
                      updateElement(selected.id, { key: safeFieldKey(event.target.value) })
                    }
                  />
                </label>
                <label>
                  <span>Element type</span>
                  <select
                    value={selected.type}
                    onChange={(event) =>
                      updateElement(selected.id, {
                        type: event.target.value as BuilderElementType,
                      })
                    }
                  >
                    <option value="text">Text</option>
                    <option value="label">Label</option>
                    <option value="verticalLabel">Vertical label</option>
                    <option value="square">Square</option>
                    <option value="circle">Circle</option>
                    <option value="line">Line</option>
                    <option value="textarea">Long text</option>
                    <option value="html">HTML</option>
                    <option value="image">Image</option>
                    <option value="staticImage">Static image</option>
                    <option value="logo">Logo</option>
                    <option value="imageCarousel">Image carousel</option>
                    <option value="textCarousel">Title and description carousel</option>
                    <option value="wordpressPosts">WordPress posts</option>
                    <option value="wordpressPostQr">WordPress post QR</option>
                    <option value="contentCarousel">Image, title, and description carousel</option>
                    <option value="ticker">News ticker</option>
                    <option value="video">Video</option>
                    <option value="youtube">YouTube</option>
                    <option value="youtubeList">YouTube List</option>
                    <option value="digitalTime">Digital time</option>
                    <option value="displayDate">Date</option>
                    <option value="analogClock">Analog clock</option>
                    <option value="qrCall">Call number QR</option>
                    <option value="qrUrl">Browse URL QR</option>
                    <option value="qrWhatsApp">WhatsApp message QR</option>
                    <option value="qrVCard">VCF contact QR</option>
                  </select>
                </label>
                <label className="checkbox-field">
                  <input
                    type="checkbox"
                    checked={selected.required}
                    disabled={[
                      "wordpressPostQr",
                      "staticImage",
                      "html",
                      "digitalTime",
                      "displayDate",
                      "analogClock",
                      "label",
                      "verticalLabel",
                      "square",
                      "circle",
                      "line",
                    ].includes(selected.type)}
                    onChange={(event) =>
                      updateElement(selected.id, { required: event.target.checked })
                    }
                  />
                  <span>
                    {[
                      "wordpressPostQr",
                      "staticImage",
                      "html",
                      "digitalTime",
                      "displayDate",
                      "analogClock",
                      "label",
                      "verticalLabel",
                      "square",
                      "circle",
                      "line",
                    ].includes(selected.type)
                      ? "Administrator-owned content"
                      : "Required content"}
                  </span>
                </label>
              </div>
              {selected.type === "staticImage" ? (
                <StaticImagePicker
                  key={selected.id}
                  busy={uploadingStaticImage}
                  assetId={selected.staticImageAssetId}
                  onChange={(id) => updateElement(selected.id, { staticImageAssetId: id })}
                  onBusyChange={setUploadingStaticImage}
                />
              ) : null}
              {selected.type === "verticalLabel" ? (
                <label>
                  <span>Vertical text style</span>
                  <select
                    value={selected.verticalLabelOrientation}
                    onChange={(event) =>
                      updateElement(selected.id, {
                        verticalLabelOrientation: event.target
                          .value as BuilderElement["verticalLabelOrientation"],
                      })
                    }
                  >
                    <option value="clockwise">Top to bottom (rotated)</option>
                    <option value="counterclockwise">Bottom to top (rotated)</option>
                    <option value="upright">Stacked upright</option>
                  </select>
                </label>
              ) : null}
              {selected.type === "wordpressPostQr" ? (
                <label>
                  <span>Linked WordPress posts widget</span>
                  <select
                    value={selected.wordpressSourceBinding}
                    onChange={(event) =>
                      updateElement(selected.id, { wordpressSourceBinding: event.target.value })
                    }
                  >
                    <option value="">Select a WordPress posts widget</option>
                    {elements
                      .filter((item) => item.type === "wordpressPosts")
                      .map((item) => (
                        <option key={item.id} value={item.key}>
                          {item.label}
                        </option>
                      ))}
                  </select>
                  <small className="field-help">
                    The QR code follows the post currently shown by this widget. Position and resize
                    this QR independently. Add a WordPress posts widget first if none is available.
                  </small>
                </label>
              ) : null}
              {[
                "text",
                "textarea",
                "label",
                "verticalLabel",
                "ticker",
                "textCarousel",
                "contentCarousel",
                "wordpressPosts",
              ].includes(selected.type) ? (
                <label>
                  <span>
                    {selected.type === "label" || selected.type === "verticalLabel"
                      ? "Label text"
                      : "Sample preview text"}
                  </span>
                  <textarea
                    aria-label={
                      selected.type === "label" || selected.type === "verticalLabel"
                        ? "Label text"
                        : "Sample preview text"
                    }
                    rows={
                      ["ticker", "textCarousel", "contentCarousel", "wordpressPosts"].includes(
                        selected.type,
                      )
                        ? 4
                        : 3
                    }
                    maxLength={2_000}
                    value={selected.previewText}
                    onChange={(event) =>
                      updateElement(selected.id, { previewText: event.target.value })
                    }
                  />
                  <small>
                    {selected.type === "textCarousel" ||
                    selected.type === "contentCarousel" ||
                    selected.type === "wordpressPosts"
                      ? `${carouselPreviewItemCount(selected.previewText)} preview item${carouselPreviewItemCount(selected.previewText) === 1 ? "" : "s"}. Use one item per line as Title | Description. Titles cannot be blank; text automatically fits inside short boxes.`
                      : selected.type === "label" || selected.type === "verticalLabel"
                        ? "Fixed slide-type text. Clients cannot edit it."
                        : "Preview-only example saved with the slide type. It never becomes client slide content."}
                  </small>
                </label>
              ) : null}
              <fieldset>
                <legend>Position and size</legend>
                <p className="geometry-help">
                  Choose percent or pixels for every dimension. Free positioning uses canvas edges;
                  middle positioning uses offsets from the exact screen center.
                </p>
                <label>
                  <span>Element position</span>
                  <select
                    aria-label="Element position"
                    value={selected.positionMode}
                    onChange={(event) => {
                      const positionMode = event.target.value as PositionMode;
                      if (positionMode === "center") {
                        const geometry = resolvedGeometry(
                          selected,
                          previewCanvasWidth,
                          previewCanvasHeight,
                        );
                        updateElement(selected.id, {
                          positionMode,
                          width: selected.width ?? geometry.width,
                          height: selected.height ?? geometry.height,
                          widthUnit: selected.width === null ? "%" : selected.widthUnit,
                          heightUnit: selected.height === null ? "%" : selected.heightUnit,
                        });
                      } else {
                        updateElement(selected.id, { positionMode });
                      }
                    }}
                  >
                    <option value="free">Free positioning from edges</option>
                    <option value="center">Middle + offset from middle</option>
                  </select>
                </label>
                <div className="geometry-grid">
                  {(selected.positionMode === "center"
                    ? ([
                        ["centerX", "Horizontal from middle", "centerXUnit"],
                        ["centerY", "Vertical from middle", "centerYUnit"],
                        ["width", "Width", "widthUnit"],
                        ["height", "Height", "heightUnit"],
                      ] as const)
                    : ([
                        ["x", "Left", "xUnit"],
                        ["right", "Right", "rightUnit"],
                        ["width", "Width", "widthUnit"],
                        ["y", "Top", "yUnit"],
                        ["bottom", "Bottom", "bottomUnit"],
                        ["height", "Height", "heightUnit"],
                      ] as const)
                  ).map(([property, label, unitProperty]) => {
                    const inputId = `${selected.id}-${property}`;
                    const isOffset = property === "centerX" || property === "centerY";
                    const unit = selected[unitProperty];
                    const canClear = !isOffset && selected.positionMode === "free";
                    return (
                      <div className="geometry-control" key={property}>
                        <label htmlFor={inputId}>{label}</label>
                        <div className="geometry-input">
                          <input
                            id={inputId}
                            type="number"
                            min={isOffset ? (unit === "%" ? -100 : -7680) : unit === "%" ? 0 : 0}
                            max={unit === "%" ? 100 : 7680}
                            step={0.5}
                            placeholder={canClear ? "Auto" : undefined}
                            value={selected[property] ?? ""}
                            onChange={(event) =>
                              updateElement(selected.id, {
                                [property]:
                                  event.target.value === ""
                                    ? canClear
                                      ? null
                                      : 0
                                    : event.target.valueAsNumber,
                              })
                            }
                          />
                          <select
                            className="input-unit-select"
                            aria-label={`${label} unit`}
                            value={unit}
                            onChange={(event) =>
                              updateElement(selected.id, {
                                [unitProperty]: event.target.value as DimensionUnit,
                              })
                            }
                          >
                            <option value="%">%</option>
                            <option value="px">px</option>
                          </select>
                          {canClear ? (
                            <button
                              type="button"
                              className="geometry-clear"
                              aria-label={`Clear ${label}`}
                              title={`Clear ${label}`}
                              disabled={selected[property] === null}
                              onClick={() => updateElement(selected.id, { [property]: null })}
                            >
                              <X size={13} />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {geometryIssue(selected, previewScreenWidth, previewScreenHeight) ? (
                  <p className="geometry-error" role="alert">
                    {geometryIssue(selected, previewScreenWidth, previewScreenHeight)}
                  </p>
                ) : null}
              </fieldset>
              <fieldset>
                <legend>Box appearance</legend>
                <div className="form-grid form-grid--four">
                  <label>
                    <span>Opacity</span>
                    <input
                      type="number"
                      min={0.1}
                      max={1}
                      step={0.05}
                      value={selected.opacity}
                      onChange={(event) =>
                        updateElement(selected.id, { opacity: event.target.valueAsNumber })
                      }
                    />
                  </label>
                  <label>
                    <span>Radius (px)</span>
                    <input
                      type="number"
                      min={0}
                      max={200}
                      value={selected.borderRadius}
                      onChange={(event) =>
                        updateElement(selected.id, {
                          borderRadius: event.target.valueAsNumber,
                        })
                      }
                    />
                  </label>
                  {widgetSpacingKeys.map((property) => {
                    const dimension = elementSpacing(selected)[property] ?? {
                      value: 0,
                      unit: "px" as const,
                    };
                    const label = property.replace(/([A-Z])/g, " $1").toLowerCase();
                    const name = label.charAt(0).toUpperCase() + label.slice(1);
                    return (
                      <label key={property}>
                        <span>{name}</span>
                        <span className="geometry-input">
                          <input
                            aria-label={name}
                            type="number"
                            min={0}
                            max={dimension.unit === "%" ? 100 : 7680}
                            value={dimension.value}
                            onChange={(event) => {
                              const value = event.target.valueAsNumber;
                              updateElement(selected.id, {
                                spacing: {
                                  ...elementSpacing(selected),
                                  [property]: {
                                    ...dimension,
                                    value: Number.isFinite(value)
                                      ? Math.max(
                                          0,
                                          Math.min(dimension.unit === "%" ? 100 : 7680, value),
                                        )
                                      : 0,
                                  },
                                },
                              });
                            }}
                          />
                          <select
                            className="input-unit-select"
                            aria-label={`${name} unit`}
                            value={dimension.unit}
                            onChange={(event) => {
                              const unit = event.target.value as DimensionUnit;
                              updateElement(selected.id, {
                                spacing: {
                                  ...elementSpacing(selected),
                                  [property]: {
                                    unit,
                                    value: Math.min(unit === "%" ? 100 : 7680, dimension.value),
                                  },
                                },
                              });
                            }}
                          >
                            <option value="%">%</option>
                            <option value="px">px</option>
                          </select>
                        </span>
                      </label>
                    );
                  })}
                  <p className="field-help">
                    Padding insets content inside the saved box. Margins follow its positioning
                    anchors; they do not move neighboring widgets.
                  </p>
                  <label>
                    <span>Background</span>
                    <select
                      value={selected.backgroundColor === "transparent" ? "transparent" : "color"}
                      onChange={(event) =>
                        updateElement(selected.id, {
                          backgroundColor:
                            event.target.value === "transparent" ? "transparent" : "#ffffff",
                        })
                      }
                    >
                      <option value="transparent">Transparent</option>
                      <option value="color">Solid color</option>
                    </select>
                  </label>
                  {selected.backgroundColor !== "transparent" ? (
                    <label>
                      <span>Background color</span>
                      <ColorPicker
                        value={selected.backgroundColor}
                        onChange={(value) => updateElement(selected.id, { backgroundColor: value })}
                      />
                    </label>
                  ) : null}
                  <label>
                    <span>Border width (px)</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={selected.borderWidth}
                      onChange={(event) =>
                        updateElement(selected.id, { borderWidth: event.target.valueAsNumber })
                      }
                    />
                  </label>
                  <label>
                    <span>Border color</span>
                    <ColorPicker
                      value={selected.borderColor}
                      onChange={(value) => updateElement(selected.id, { borderColor: value })}
                    />
                  </label>
                  <label>
                    <span>Entrance animation</span>
                    <select
                      value={selected.animation}
                      onChange={(event) =>
                        updateElement(selected.id, {
                          animation: event.target.value as BuilderElement["animation"],
                        })
                      }
                    >
                      <option value="none">None</option>
                      <option value="fade-in">Fade in</option>
                      <option value="blur-in">Blur in</option>
                      <option value="slide-in-left">Slide from left</option>
                      <option value="slide-in-right">Slide from right</option>
                      <option value="slide-in-up">Slide from bottom</option>
                      <option value="slide-in-down">Slide from top</option>
                      <option value="zoom-in-subtle">Subtle zoom</option>
                      <option value="zoom-in">Zoom in</option>
                      <option value="bounce-in">Bounce in</option>
                      <option value="rotate-in">Rotate in</option>
                      <option value="flip-in">Flip in</option>
                      <option value="reveal-up">Reveal upward</option>
                    </select>
                  </label>
                  <label>
                    <span>Animation duration (ms)</span>
                    <input
                      type="number"
                      min={100}
                      max={10000}
                      step={50}
                      value={selected.animationDurationMs}
                      onChange={(event) =>
                        updateElement(selected.id, {
                          animationDurationMs: event.target.valueAsNumber,
                        })
                      }
                    />
                  </label>
                  <label>
                    <span>Animation delay (ms)</span>
                    <input
                      type="number"
                      min={0}
                      max={10000}
                      step={50}
                      value={selected.animationDelayMs}
                      onChange={(event) =>
                        updateElement(selected.id, {
                          animationDelayMs: event.target.valueAsNumber,
                        })
                      }
                    />
                  </label>
                  <label>
                    <span>Animation repeat</span>
                    <select
                      value={selected.animationIteration}
                      onChange={(event) =>
                        updateElement(selected.id, {
                          animationIteration: event.target
                            .value as BuilderElement["animationIteration"],
                        })
                      }
                    >
                      <option value="once">Once</option>
                      <option value="infinite">Continuous</option>
                    </select>
                  </label>
                </div>
              </fieldset>
              {selected.type === "html" ? (
                <fieldset>
                  <legend>Administrator HTML document</legend>
                  <p className="geometry-help">
                    Enter a complete document, including doctype, html, head, styles, and body.
                    Scripts, forms, frames, event handlers, and executable URLs remain blocked.
                    Clients cannot view or edit this source.
                  </p>
                  <label>
                    <span>HTML source</span>
                    <textarea
                      className="html-source-input"
                      value={selected.htmlContent}
                      maxLength={500_000}
                      spellCheck={false}
                      onChange={(event) =>
                        updateElement(selected.id, { htmlContent: event.target.value })
                      }
                    />
                  </label>
                </fieldset>
              ) : null}
              {selected.type === "ticker" ? (
                <fieldset>
                  <legend>Ticker motion and separators</legend>
                  <p className="geometry-help">
                    Speed is measured in pixels per second, so the motion stays consistent when
                    headlines or fonts change.
                  </p>
                  <div className="form-grid form-grid--four">
                    <label>
                      <span>Scroll direction</span>
                      <select
                        value={selected.tickerDirection}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            tickerDirection: event.target
                              .value as BuilderElement["tickerDirection"],
                          })
                        }
                      >
                        <option value="left">Move left</option>
                        <option value="right">Move right</option>
                      </select>
                    </label>
                    <label>
                      <span>Scroll speed (px/s)</span>
                      <input
                        type="number"
                        min={10}
                        max={500}
                        step={5}
                        value={selected.tickerSpeed}
                        onChange={(event) =>
                          updateElement(selected.id, { tickerSpeed: event.target.valueAsNumber })
                        }
                      />
                    </label>
                    <label>
                      <span>Vertical alignment</span>
                      <select
                        value={selected.tickerVerticalAlign}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            tickerVerticalAlign: event.target
                              .value as BuilderElement["tickerVerticalAlign"],
                          })
                        }
                      >
                        <option value="flex-start">Top</option>
                        <option value="center">Middle</option>
                        <option value="flex-end">Bottom</option>
                      </select>
                    </label>
                    <label>
                      <span>Headline separator</span>
                      <input
                        maxLength={24}
                        value={selected.tickerSeparator}
                        onChange={(event) =>
                          updateElement(selected.id, { tickerSeparator: event.target.value })
                        }
                      />
                    </label>
                    <label>
                      <span>Separator color</span>
                      <ColorPicker
                        value={selected.tickerSeparatorColor}
                        onChange={(value) =>
                          updateElement(selected.id, { tickerSeparatorColor: value })
                        }
                      />
                    </label>
                    <label>
                      <span>Separator spacing (px)</span>
                      <input
                        type="number"
                        min={0}
                        max={400}
                        value={selected.tickerSeparatorSpacing}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            tickerSeparatorSpacing: event.target.valueAsNumber,
                          })
                        }
                      />
                    </label>
                    <label>
                      <span>Sequence gap (px)</span>
                      <input
                        type="number"
                        min={0}
                        max={1000}
                        value={selected.tickerSequenceGap}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            tickerSequenceGap: event.target.valueAsNumber,
                          })
                        }
                      />
                    </label>
                  </div>
                </fieldset>
              ) : null}
              {["imageCarousel", "textCarousel", "contentCarousel", "wordpressPosts"].includes(
                selected.type,
              ) ? (
                <>
                  <fieldset>
                    <legend>Carousel motion and navigation</legend>
                    {selected.type === "wordpressPosts" ? (
                      <label>
                        <span>Post excerpts</span>
                        <select
                          value={selected.carousel.showExcerpt === false ? "hide" : "show"}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              showExcerpt: event.target.value === "show",
                            })
                          }
                        >
                          <option value="show">Shown</option>
                          <option value="hide">Hidden — titles only</option>
                        </select>
                      </label>
                    ) : null}
                    <p className="geometry-help">
                      These bounded settings are owned by the slide type. Clients choose the
                      WordPress connection or add and order manual carousel items.
                    </p>
                    <div className="form-grid form-grid--four">
                      <label>
                        <span>Autoplay</span>
                        <select
                          aria-label="Carousel autoplay"
                          value={selected.carousel.autoplay ? "on" : "off"}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              autoplay: event.target.value === "on",
                            })
                          }
                        >
                          <option value="on">Automatic</option>
                          <option value="off">Manual only</option>
                        </select>
                      </label>
                      <label>
                        <span>Item duration (ms)</span>
                        <input
                          type="number"
                          min={1000}
                          max={60000}
                          step={250}
                          value={selected.carousel.intervalMs}
                          onChange={(event) =>
                            updateCarousel(selected.id, { intervalMs: event.target.valueAsNumber })
                          }
                        />
                      </label>
                      <label>
                        <span>Transition</span>
                        <select
                          value={selected.carousel.transition}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              transition: event.target.value as CarouselSettings["transition"],
                            })
                          }
                        >
                          <option value="slide">Slide</option>
                          <option value="fade">Fade</option>
                          <option value="zoom">Zoom in</option>
                          <option value="flip">3D flip</option>
                          <option value="blur">Blur reveal</option>
                          <option value="rotate">Rotate in</option>
                          <option value="bounce">Bounce in</option>
                        </select>
                      </label>
                      <label>
                        <span>Transition duration (ms)</span>
                        <input
                          type="number"
                          min={100}
                          max={5000}
                          step={50}
                          value={selected.carousel.transitionDurationMs}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              transitionDurationMs: event.target.valueAsNumber,
                            })
                          }
                        />
                      </label>
                      <label>
                        <span>Direction</span>
                        <select
                          value={selected.carousel.direction}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              direction: event.target.value as CarouselSettings["direction"],
                            })
                          }
                        >
                          <option value="left">Move left</option>
                          <option value="right">Move right</option>
                          <option value="up">Move up</option>
                          <option value="down">Move down</option>
                        </select>
                      </label>
                      <label>
                        <span>End behavior</span>
                        <select
                          value={selected.carousel.loop ? "loop" : "stop"}
                          onChange={(event) =>
                            updateCarousel(selected.id, { loop: event.target.value === "loop" })
                          }
                        >
                          <option value="loop">Loop continuously</option>
                          <option value="stop">Stop at last item</option>
                        </select>
                      </label>
                      <label>
                        <span>Indicators</span>
                        <select
                          value={selected.carousel.showIndicators ? "show" : "hide"}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              showIndicators: event.target.value === "show",
                            })
                          }
                        >
                          <option value="show">Show</option>
                          <option value="hide">Hide</option>
                        </select>
                      </label>
                      <label>
                        <span>Indicator position</span>
                        <select
                          value={selected.carousel.indicatorPosition}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              indicatorPosition: event.target
                                .value as CarouselSettings["indicatorPosition"],
                            })
                          }
                        >
                          <option value="top">Top</option>
                          <option value="bottom">Bottom</option>
                        </select>
                      </label>
                      <label>
                        <span>Indicator shape</span>
                        <select
                          value={selected.carousel.indicatorStyle}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              indicatorStyle: event.target
                                .value as CarouselSettings["indicatorStyle"],
                            })
                          }
                        >
                          <option value="dots">Dots</option>
                          <option value="bars">Bars</option>
                        </select>
                      </label>
                      <label>
                        <span>Indicator color</span>
                        <ColorPicker
                          value={selected.carousel.indicatorColor}
                          onChange={(value) =>
                            updateCarousel(selected.id, { indicatorColor: value })
                          }
                        />
                      </label>
                      <label>
                        <span>Active indicator color</span>
                        <ColorPicker
                          value={selected.carousel.activeIndicatorColor}
                          onChange={(value) =>
                            updateCarousel(selected.id, { activeIndicatorColor: value })
                          }
                        />
                      </label>
                      <label>
                        <span>Indicator size (px)</span>
                        <input
                          type="number"
                          min={3}
                          max={40}
                          value={selected.carousel.indicatorSize}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              indicatorSize: event.target.valueAsNumber,
                            })
                          }
                        />
                      </label>
                      <label>
                        <span>Indicator gap (px)</span>
                        <input
                          type="number"
                          min={0}
                          max={60}
                          value={selected.carousel.indicatorGap}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              indicatorGap: event.target.valueAsNumber,
                            })
                          }
                        />
                      </label>
                      <label>
                        <span>Previous/next arrows</span>
                        <select
                          value={selected.carousel.showArrows ? "show" : "hide"}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              showArrows: event.target.value === "show",
                            })
                          }
                        >
                          <option value="hide">Hide</option>
                          <option value="show">Show</option>
                        </select>
                      </label>
                      <label>
                        <span>Arrow color</span>
                        <ColorPicker
                          value={selected.carousel.arrowColor}
                          onChange={(value) => updateCarousel(selected.id, { arrowColor: value })}
                        />
                      </label>
                      <label>
                        <span>Arrow background</span>
                        <ColorPicker
                          value={selected.carousel.arrowBackgroundColor}
                          onChange={(value) =>
                            updateCarousel(selected.id, { arrowBackgroundColor: value })
                          }
                        />
                      </label>
                      <label>
                        <span>Arrow size (px)</span>
                        <input
                          type="number"
                          min={12}
                          max={120}
                          value={selected.carousel.arrowSize}
                          onChange={(event) =>
                            updateCarousel(selected.id, { arrowSize: event.target.valueAsNumber })
                          }
                        />
                      </label>
                    </div>
                  </fieldset>
                  <fieldset>
                    <legend>Carousel card and image</legend>
                    <div className="form-grid form-grid--four">
                      <label>
                        <span>Card background</span>
                        <ColorPicker
                          value={selected.carousel.cardBackgroundColor}
                          onChange={(value) =>
                            updateCarousel(selected.id, { cardBackgroundColor: value })
                          }
                        />
                      </label>
                      <label>
                        <span>Card border color</span>
                        <ColorPicker
                          value={selected.carousel.cardBorderColor}
                          onChange={(value) =>
                            updateCarousel(selected.id, { cardBorderColor: value })
                          }
                        />
                      </label>
                      <label>
                        <span>Card border width (px)</span>
                        <input
                          type="number"
                          min={0}
                          max={30}
                          value={selected.carousel.cardBorderWidth}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              cardBorderWidth: event.target.valueAsNumber,
                            })
                          }
                        />
                      </label>
                      <label>
                        <span>Card radius (px)</span>
                        <input
                          type="number"
                          min={0}
                          max={300}
                          value={selected.carousel.cardBorderRadius}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              cardBorderRadius: event.target.valueAsNumber,
                            })
                          }
                        />
                      </label>
                      <label>
                        <span>Card padding (px)</span>
                        <input
                          type="number"
                          min={0}
                          max={200}
                          value={selected.carousel.cardPadding}
                          onChange={(event) =>
                            updateCarousel(selected.id, {
                              cardPadding: event.target.valueAsNumber,
                            })
                          }
                        />
                      </label>
                      {selected.type !== "textCarousel" && selected.type !== "wordpressPosts" ? (
                        <>
                          <label>
                            <span>Image fit</span>
                            <select
                              value={selected.carousel.imageFit}
                              onChange={(event) =>
                                updateCarousel(selected.id, {
                                  imageFit: event.target.value as CarouselSettings["imageFit"],
                                })
                              }
                            >
                              <option value="cover">Cover</option>
                              <option value="contain">Contain</option>
                              <option value="fill">Stretch</option>
                            </select>
                          </label>
                          <label>
                            <span>Image focus</span>
                            <select
                              value={selected.carousel.imagePosition}
                              onChange={(event) =>
                                updateCarousel(selected.id, {
                                  imagePosition: event.target
                                    .value as CarouselSettings["imagePosition"],
                                })
                              }
                            >
                              {mediaPositions.map((position) => (
                                <option key={position} value={position}>
                                  {position}
                                </option>
                              ))}
                            </select>
                          </label>
                        </>
                      ) : null}
                      {selected.type === "contentCarousel" ? (
                        <>
                          <label>
                            <span>Content layout</span>
                            <select
                              value={selected.carousel.contentLayout}
                              onChange={(event) =>
                                updateCarousel(selected.id, {
                                  contentLayout: event.target
                                    .value as CarouselSettings["contentLayout"],
                                })
                              }
                            >
                              <option value="top">Image above text</option>
                              <option value="bottom">Image below text</option>
                              <option value="left">Image left</option>
                              <option value="right">Image right</option>
                              <option value="background">Image background</option>
                            </select>
                          </label>
                          <label>
                            <span>Image area (%)</span>
                            <input
                              type="number"
                              min={20}
                              max={80}
                              value={selected.carousel.imageAreaPercent}
                              onChange={(event) =>
                                updateCarousel(selected.id, {
                                  imageAreaPercent: event.target.valueAsNumber,
                                })
                              }
                            />
                          </label>
                        </>
                      ) : null}
                    </div>
                  </fieldset>
                  {selected.type !== "imageCarousel" ? (
                    <fieldset>
                      <legend>Carousel text</legend>
                      <div className="form-grid form-grid--four">
                        <label>
                          <span>Content alignment</span>
                          <select
                            value={selected.carousel.contentAlign}
                            onChange={(event) =>
                              updateCarousel(selected.id, {
                                contentAlign: event.target
                                  .value as CarouselSettings["contentAlign"],
                              })
                            }
                          >
                            <option value="start">Start</option>
                            <option value="center">Center</option>
                            <option value="end">End</option>
                          </select>
                        </label>
                        <label>
                          <span>Title/description gap (px)</span>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={selected.carousel.textGap}
                            onChange={(event) =>
                              updateCarousel(selected.id, { textGap: event.target.valueAsNumber })
                            }
                          />
                        </label>
                        {(["title", "description"] as const).flatMap((target) => [
                          <label key={`${target}-color`}>
                            <span>{target === "title" ? "Title" : "Description"} color</span>
                            <ColorPicker
                              value={selected.carousel[target].color}
                              onChange={(value) =>
                                updateCarouselText(selected.id, target, { color: value })
                              }
                            />
                          </label>,
                          <label key={`${target}-font`}>
                            <span>{target === "title" ? "Title" : "Description"} font</span>
                            <select
                              value={selected.carousel[target].fontFamily}
                              onChange={(event) =>
                                updateCarouselText(selected.id, target, {
                                  fontFamily: event.target.value,
                                })
                              }
                            >
                              {fontFamilies.map((font) => (
                                <option key={font} value={font}>
                                  {font}
                                </option>
                              ))}
                            </select>
                          </label>,
                          <label key={`${target}-size`}>
                            <span>{target === "title" ? "Title" : "Description"} size (px)</span>
                            <input
                              type="number"
                              min={8}
                              max={300}
                              value={selected.carousel[target].fontSize}
                              onChange={(event) =>
                                updateCarouselText(selected.id, target, {
                                  fontSize: event.target.valueAsNumber,
                                })
                              }
                            />
                          </label>,
                          <label key={`${target}-weight`}>
                            <span>{target === "title" ? "Title" : "Description"} weight</span>
                            <select
                              value={selected.carousel[target].fontWeight}
                              onChange={(event) =>
                                updateCarouselText(selected.id, target, {
                                  fontWeight: Number(event.target.value),
                                })
                              }
                            >
                              <option value="300">Light</option>
                              <option value="400">Regular</option>
                              <option value="600">Semibold</option>
                              <option value="700">Bold</option>
                              <option value="900">Black</option>
                            </select>
                          </label>,
                          <label key={`${target}-style`}>
                            <span>{target === "title" ? "Title" : "Description"} style</span>
                            <select
                              value={selected.carousel[target].fontStyle}
                              onChange={(event) =>
                                updateCarouselText(selected.id, target, {
                                  fontStyle: event.target
                                    .value as CarouselSettings["title"]["fontStyle"],
                                })
                              }
                            >
                              <option value="normal">Normal</option>
                              <option value="italic">Italic</option>
                            </select>
                          </label>,
                          <label key={`${target}-decoration`}>
                            <span>{target === "title" ? "Title" : "Description"} decoration</span>
                            <select
                              value={selected.carousel[target].textDecoration}
                              onChange={(event) =>
                                updateCarouselText(selected.id, target, {
                                  textDecoration: event.target
                                    .value as CarouselSettings["title"]["textDecoration"],
                                })
                              }
                            >
                              <option value="none">None</option>
                              <option value="underline">Underline</option>
                              <option value="line-through">Strikethrough</option>
                            </select>
                          </label>,
                          <label key={`${target}-transform`}>
                            <span>{target === "title" ? "Title" : "Description"} case</span>
                            <select
                              value={selected.carousel[target].textTransform}
                              onChange={(event) =>
                                updateCarouselText(selected.id, target, {
                                  textTransform: event.target
                                    .value as CarouselSettings["title"]["textTransform"],
                                })
                              }
                            >
                              <option value="none">As entered</option>
                              <option value="uppercase">Uppercase</option>
                              <option value="lowercase">Lowercase</option>
                              <option value="capitalize">Capitalize</option>
                            </select>
                          </label>,
                          <label key={`${target}-letter-spacing`}>
                            <span>
                              {target === "title" ? "Title" : "Description"} letter spacing (px)
                            </span>
                            <input
                              type="number"
                              min={-10}
                              max={50}
                              step={0.5}
                              value={selected.carousel[target].letterSpacing}
                              onChange={(event) =>
                                updateCarouselText(selected.id, target, {
                                  letterSpacing: event.target.valueAsNumber,
                                })
                              }
                            />
                          </label>,
                          <label key={`${target}-line-height`}>
                            <span>{target === "title" ? "Title" : "Description"} line height</span>
                            <input
                              type="number"
                              min={0.5}
                              max={4}
                              step={0.05}
                              value={selected.carousel[target].lineHeight}
                              onChange={(event) =>
                                updateCarouselText(selected.id, target, {
                                  lineHeight: event.target.valueAsNumber,
                                })
                              }
                            />
                          </label>,
                          <label key={`${target}-align`}>
                            <span>{target === "title" ? "Title" : "Description"} alignment</span>
                            <select
                              value={selected.carousel[target].textAlign}
                              onChange={(event) =>
                                updateCarouselText(selected.id, target, {
                                  textAlign: event.target
                                    .value as CarouselSettings["title"]["textAlign"],
                                })
                              }
                            >
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </select>
                          </label>,
                        ])}
                      </div>
                    </fieldset>
                  ) : null}
                </>
              ) : null}
              {selected.type === "digitalTime" ? (
                <fieldset>
                  <legend>Digital clock format</legend>
                  <div className="form-grid">
                    <label>
                      <span>Seconds</span>
                      <select
                        aria-label="Digital clock seconds"
                        value={selected.digitalShowSeconds ? "show" : "hide"}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            digitalShowSeconds: event.target.value === "show",
                          })
                        }
                      >
                        <option value="show">Show seconds</option>
                        <option value="hide">Hide seconds</option>
                      </select>
                    </label>
                    <label>
                      <span>Hour format</span>
                      <select
                        aria-label="Digital clock hour format"
                        value={selected.digitalUse24Hour ? "24" : "12"}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            digitalUse24Hour: event.target.value === "24",
                          })
                        }
                      >
                        <option value="12">12-hour</option>
                        <option value="24">24-hour</option>
                      </select>
                    </label>
                  </div>
                </fieldset>
              ) : null}
              {selected.type === "displayDate" ? (
                <fieldset>
                  <legend>Date format</legend>
                  <label>
                    <span>Format text</span>
                    <input
                      type="text"
                      value={selected.dateFormat}
                      maxLength={120}
                      placeholder="dddd, MMMM D, YYYY"
                      onChange={(event) =>
                        updateElement(selected.id, { dateFormat: event.target.value })
                      }
                    />
                  </label>
                  <p className="field-help">
                    Tokens: YYYY, YY, MMMM, MMM, MM, M, DD, D, dddd, ddd.
                  </p>
                </fieldset>
              ) : null}
              {selected.type === "text" ||
              selected.type === "textarea" ||
              selected.type === "label" ||
              selected.type === "verticalLabel" ||
              selected.type === "ticker" ||
              selected.type === "digitalTime" ||
              selected.type === "displayDate" ? (
                <fieldset>
                  <legend>Typography</legend>
                  <div className="form-grid form-grid--four">
                    <label>
                      <span>Text color</span>
                      <ColorPicker
                        value={selected.color}
                        onChange={(value) => updateElement(selected.id, { color: value })}
                      />
                    </label>
                    <label>
                      <span>Font size (px)</span>
                      <input
                        type="number"
                        min={8}
                        max={300}
                        value={selected.fontSize}
                        onChange={(event) =>
                          updateElement(selected.id, { fontSize: event.target.valueAsNumber })
                        }
                      />
                    </label>
                    <label>
                      <span>Weight</span>
                      <select
                        value={selected.fontWeight}
                        onChange={(event) =>
                          updateElement(selected.id, { fontWeight: Number(event.target.value) })
                        }
                      >
                        <option value="300">Light</option>
                        <option value="400">Regular</option>
                        <option value="600">Semibold</option>
                        <option value="700">Bold</option>
                        <option value="900">Black</option>
                      </select>
                    </label>
                    <label>
                      <span>Align</span>
                      <select
                        value={selected.textAlign}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            textAlign: event.target.value as BuilderElement["textAlign"],
                          })
                        }
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </label>
                    <label>
                      <span>Font family</span>
                      <select
                        value={selected.fontFamily}
                        onChange={(event) =>
                          updateElement(selected.id, { fontFamily: event.target.value })
                        }
                      >
                        {fontFamilies.map((font) => (
                          <option key={font} value={font}>
                            {font}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Font style</span>
                      <select
                        value={selected.fontStyle}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            fontStyle: event.target.value as BuilderElement["fontStyle"],
                          })
                        }
                      >
                        <option value="normal">Normal</option>
                        <option value="italic">Italic</option>
                      </select>
                    </label>
                    <label>
                      <span>Decoration</span>
                      <select
                        value={selected.textDecoration}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            textDecoration: event.target.value as BuilderElement["textDecoration"],
                          })
                        }
                      >
                        <option value="none">None</option>
                        <option value="underline">Underline</option>
                      </select>
                    </label>
                    <label>
                      <span>Capitalization</span>
                      <select
                        value={selected.textTransform}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            textTransform: event.target.value as BuilderElement["textTransform"],
                          })
                        }
                      >
                        <option value="none">As entered</option>
                        <option value="uppercase">Uppercase</option>
                        <option value="lowercase">Lowercase</option>
                        <option value="capitalize">Capitalize words</option>
                      </select>
                    </label>
                    <label>
                      <span>Line height</span>
                      <input
                        type="number"
                        min={0.5}
                        max={4}
                        step={0.1}
                        value={selected.lineHeight}
                        onChange={(event) =>
                          updateElement(selected.id, { lineHeight: event.target.valueAsNumber })
                        }
                      />
                    </label>
                    <label>
                      <span>Letter spacing (px)</span>
                      <input
                        type="number"
                        min={-20}
                        max={100}
                        value={selected.letterSpacing}
                        onChange={(event) =>
                          updateElement(selected.id, {
                            letterSpacing: event.target.valueAsNumber,
                          })
                        }
                      />
                    </label>
                    {!["ticker", "digitalTime", "displayDate"].includes(selected.type) ? (
                      <label>
                        <span>Text sizing</span>
                        <select
                          value={selected.textFit}
                          onChange={(event) =>
                            updateElement(selected.id, {
                              textFit: event.target.value as BuilderElement["textFit"],
                            })
                          }
                        >
                          <option value="fixed">Fixed font size</option>
                          <option value="shrink">Shrink to fit box</option>
                        </select>
                      </label>
                    ) : null}
                    {!["ticker", "digitalTime", "displayDate"].includes(selected.type) &&
                    selected.textFit === "shrink" ? (
                      <label>
                        <span>Minimum font size</span>
                        <input
                          type="number"
                          min={6}
                          max={200}
                          value={selected.minFontSize}
                          onChange={(event) =>
                            updateElement(selected.id, {
                              minFontSize: event.target.valueAsNumber,
                            })
                          }
                        />
                      </label>
                    ) : null}
                    {!["ticker", "digitalTime", "displayDate"].includes(selected.type) ? (
                      <label>
                        <span>Text wrapping</span>
                        <select
                          value={selected.textWrap}
                          onChange={(event) =>
                            updateElement(selected.id, {
                              textWrap: event.target.value as BuilderElement["textWrap"],
                            })
                          }
                        >
                          <option value="wrap">Wrap words</option>
                          <option value="preserve">Preserve line breaks</option>
                          <option value="nowrap">Single line</option>
                        </select>
                      </label>
                    ) : null}
                  </div>
                </fieldset>
              ) : selected.type === "qrCall" ||
                selected.type === "qrUrl" ||
                selected.type === "wordpressPostQr" ||
                selected.type === "qrWhatsApp" ||
                selected.type === "qrVCard" ||
                selected.type === "analogClock" ? (
                <fieldset>
                  <legend>
                    {selected.type === "analogClock" ? "Clock appearance" : "QR appearance"}
                  </legend>
                  <div className="form-grid">
                    <label>
                      <span>
                        {selected.type === "analogClock" ? "Clock color" : "QR foreground"}
                      </span>
                      <ColorPicker
                        value={selected.color}
                        onChange={(value) => updateElement(selected.id, { color: value })}
                      />
                    </label>
                    {selected.type === "analogClock" ? (
                      <>
                        <label>
                          <span>Hour markers</span>
                          <select
                            aria-label="Hour markers visibility"
                            value={selected.analogShowHourMarkers ? "show" : "hide"}
                            onChange={(event) =>
                              updateElement(selected.id, {
                                analogShowHourMarkers: event.target.value === "show",
                              })
                            }
                          >
                            <option value="show">Show</option>
                            <option value="hide">Hide</option>
                          </select>
                        </label>
                        <label>
                          <span>Clock numbers</span>
                          <select
                            aria-label="Clock numbers visibility"
                            value={selected.analogShowNumbers ? "show" : "hide"}
                            onChange={(event) =>
                              updateElement(selected.id, {
                                analogShowNumbers: event.target.value === "show",
                              })
                            }
                          >
                            <option value="hide">Hide</option>
                            <option value="show">Show 1–12</option>
                          </select>
                        </label>
                        {selected.analogShowNumbers ? (
                          <>
                            <label>
                              <span>Number color</span>
                              <ColorPicker
                                value={selected.analogNumberColor}
                                onChange={(value) =>
                                  updateElement(selected.id, { analogNumberColor: value })
                                }
                              />
                            </label>
                            <label>
                              <span>Number font</span>
                              <select
                                value={selected.analogNumberFontFamily}
                                onChange={(event) =>
                                  updateElement(selected.id, {
                                    analogNumberFontFamily: event.target.value,
                                  })
                                }
                              >
                                {fontFamilies.map((font) => (
                                  <option key={font} value={font}>
                                    {font}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              <span>Number size (px)</span>
                              <input
                                type="number"
                                min={6}
                                max={80}
                                value={selected.analogNumberFontSize}
                                onChange={(event) =>
                                  updateElement(selected.id, {
                                    analogNumberFontSize: event.target.valueAsNumber,
                                  })
                                }
                              />
                            </label>
                            <label>
                              <span>Number weight</span>
                              <select
                                value={selected.analogNumberFontWeight}
                                onChange={(event) =>
                                  updateElement(selected.id, {
                                    analogNumberFontWeight: Number(event.target.value),
                                  })
                                }
                              >
                                <option value="300">Light</option>
                                <option value="400">Regular</option>
                                <option value="600">Semibold</option>
                                <option value="700">Bold</option>
                                <option value="900">Black</option>
                              </select>
                            </label>
                            <label>
                              <span>Number radius (%)</span>
                              <input
                                type="number"
                                min={25}
                                max={44}
                                value={selected.analogNumberRadius}
                                onChange={(event) =>
                                  updateElement(selected.id, {
                                    analogNumberRadius: event.target.valueAsNumber,
                                  })
                                }
                              />
                            </label>
                          </>
                        ) : null}
                        <label>
                          <span>Marker color</span>
                          <ColorPicker
                            value={selected.analogMarkerColor}
                            onChange={(value) =>
                              updateElement(selected.id, { analogMarkerColor: value })
                            }
                          />
                        </label>
                        <label>
                          <span>Marker length (%)</span>
                          <input
                            type="number"
                            min={2}
                            max={20}
                            value={selected.analogMarkerLength}
                            onChange={(event) =>
                              updateElement(selected.id, {
                                analogMarkerLength: event.target.valueAsNumber,
                              })
                            }
                          />
                        </label>
                        <label>
                          <span>Hour hand color</span>
                          <ColorPicker
                            value={selected.analogHourHandColor}
                            onChange={(value) =>
                              updateElement(selected.id, { analogHourHandColor: value })
                            }
                          />
                        </label>
                        <label>
                          <span>Hour hand length (%)</span>
                          <input
                            type="number"
                            min={10}
                            max={45}
                            value={selected.analogHourHandLength}
                            onChange={(event) =>
                              updateElement(selected.id, {
                                analogHourHandLength: event.target.valueAsNumber,
                              })
                            }
                          />
                        </label>
                        <label>
                          <span>Minute hand color</span>
                          <ColorPicker
                            value={selected.analogMinuteHandColor}
                            onChange={(value) =>
                              updateElement(selected.id, { analogMinuteHandColor: value })
                            }
                          />
                        </label>
                        <label>
                          <span>Minute hand length (%)</span>
                          <input
                            type="number"
                            min={10}
                            max={48}
                            value={selected.analogMinuteHandLength}
                            onChange={(event) =>
                              updateElement(selected.id, {
                                analogMinuteHandLength: event.target.valueAsNumber,
                              })
                            }
                          />
                        </label>
                        <label>
                          <span>Second hand</span>
                          <select
                            aria-label="Second hand visibility"
                            value={selected.analogShowSecondHand ? "show" : "hide"}
                            onChange={(event) =>
                              updateElement(selected.id, {
                                analogShowSecondHand: event.target.value === "show",
                              })
                            }
                          >
                            <option value="show">Show</option>
                            <option value="hide">Hide</option>
                          </select>
                        </label>
                        <label>
                          <span>Second hand color</span>
                          <ColorPicker
                            value={selected.analogSecondHandColor}
                            onChange={(value) =>
                              updateElement(selected.id, { analogSecondHandColor: value })
                            }
                          />
                        </label>
                        <label>
                          <span>Second hand length (%)</span>
                          <input
                            type="number"
                            min={10}
                            max={49}
                            value={selected.analogSecondHandLength}
                            onChange={(event) =>
                              updateElement(selected.id, {
                                analogSecondHandLength: event.target.valueAsNumber,
                              })
                            }
                          />
                        </label>
                        <label>
                          <span>Hand thickness (%)</span>
                          <input
                            type="number"
                            min={0.5}
                            max={8}
                            step={0.1}
                            value={selected.analogHandThickness}
                            onChange={(event) =>
                              updateElement(selected.id, {
                                analogHandThickness: event.target.valueAsNumber,
                              })
                            }
                          />
                        </label>
                      </>
                    ) : (
                      <p className="muted">
                        Keep strong contrast between the QR foreground and its box background for
                        reliable scanning.
                      </p>
                    )}
                  </div>
                </fieldset>
              ) : (
                <div className="form-grid">
                  <label>
                    <span>Media fit</span>
                    <select
                      value={selected.objectFit}
                      onChange={(event) =>
                        updateElement(selected.id, {
                          objectFit: event.target.value as BuilderElement["objectFit"],
                        })
                      }
                    >
                      <option value="cover">Cover box</option>
                      <option value="contain">Contain inside box</option>
                      <option value="fill">Stretch to box</option>
                    </select>
                  </label>
                  <label>
                    <span>Media position</span>
                    <select
                      value={selected.objectPosition}
                      onChange={(event) =>
                        updateElement(selected.id, { objectPosition: event.target.value })
                      }
                    >
                      {mediaPositions.map((position) => (
                        <option key={position} value={position}>
                          {position}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </section>
          ) : (
            <div className="preview-empty">Add or select an element to edit its box.</div>
          )}
        </aside>
      </div>

      {valid ? (
        <div className="notice notice--good">
          <Check size={17} />
          <span>The complete slide-type design passes the safe renderer schema.</span>
        </div>
      ) : (
        <div className="notice notice--error" role="alert">
          <CircleAlert size={17} />
          <span>
            {duplicateKey
              ? "Every element must have a unique binding key."
              : firstGeometryIssue
                ? firstGeometryIssue
                : parsed.success
                  ? "Add at least one element."
                  : parsed.error.issues[0]?.message}
          </span>
        </div>
      )}
      {error ? <div className="form-error">{error}</div> : null}
      <details className="generated-json">
        <summary>Inspect complete versioned design JSON</summary>
        <pre>{JSON.stringify(definition, null, 2)}</pre>
      </details>
      <div className="modal-actions">
        <button type="button" className="button button--secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={!valid || saving || uploadingStaticImage}
          onClick={() => valid && onSave(definition)}
        >
          {uploadingStaticImage
            ? "Uploading image…"
            : saving
              ? "Saving…"
              : isCreating
                ? "Create slide type"
                : "Save new version"}
        </button>
      </div>
    </div>
  );
}
